import { masterTypeName } from '@/common/app/modules/master'
import { MasterType } from '@/common/model/master/w2Master'

export type CsvColumn = { name: string; required: boolean; desc: string }
export type CsvFormat = {
  type: MasterType
  label: string
  filePrefix: string
  description: string
  behavior: string
  columns: CsvColumn[]
  sample: string
}

// ラベルは masterTypeName を単一ソースとするため、ここでは持たない（W2接頭辞も masterTypeName 側で統一）
const RAW_CSV_FORMATS: Omit<CsvFormat, 'label'>[] = [
  {
    type: 'orders',
    filePrefix: 'orders_',
    description:
      '注文データを取り込みます。同じ受注番号+商品IDの組み合わせが既存の場合は更新されます。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'order_datetime', required: true, desc: '注文日時（例: 2026-02-28 10:30:00）' },
      { name: 'customer_id', required: true, desc: '顧客ID' },
      { name: 'order_number', required: true, desc: '注文番号（受注番号+商品IDで一意）' },
      { name: 'item_id', required: true, desc: '商品ID（external_id）' },
      { name: 'unit_price', required: false, desc: '単価（数値）' },
      { name: 'quantity', required: false, desc: '数量（整数）' }
    ],
    sample:
      'order_datetime,customer_id,order_number,item_id,unit_price,quantity\n2026-02-28 10:30:00,CUST-001,ORD-10001,SKU-001,2980,1\n2026-02-28 10:30:00,CUST-001,ORD-10001,SKU-002,5980,2\n2026-02-28 11:15:00,CUST-002,ORD-10002,SKU-001,2980,3'
  },
  {
    type: 'w2_User',
    filePrefix: 'w2_user_',
    description: 'W2ユーザーマスタ（全89項目）を登録・更新します。user_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'user_id', required: true, desc: 'ユーザーID（PK）' },
      { name: 'user_kbn', required: false, desc: '顧客区分' },
      { name: 'mall_id', required: false, desc: 'モールID' },
      { name: 'name', required: false, desc: '氏名' },
      { name: 'name1', required: false, desc: '氏名1（姓）' },
      { name: 'name2', required: false, desc: '氏名2（名）' },
      { name: 'name_kana', required: false, desc: '氏名かな' },
      { name: 'name_kana1', required: false, desc: '氏名かな1' },
      { name: 'name_kana2', required: false, desc: '氏名かな2' },
      { name: 'nick_name', required: false, desc: 'ニックネーム' },
      { name: 'mail_addr', required: false, desc: 'メールアドレス' },
      { name: 'zip', required: false, desc: '郵便番号' },
      { name: 'zip1', required: false, desc: '郵便番号1' },
      { name: 'zip2', required: false, desc: '郵便番号2' },
      { name: 'addr', required: false, desc: '住所' },
      { name: 'addr1', required: false, desc: '住所1' },
      { name: 'addr2', required: false, desc: '住所2' },
      { name: 'addr3', required: false, desc: '住所3' },
      { name: 'addr4', required: false, desc: '住所4' },
      { name: 'addr5', required: false, desc: '住所5' },
      { name: 'tel1', required: false, desc: '電話番号1' },
      { name: 'tel1_1', required: false, desc: '電話番号1-1' },
      { name: 'tel1_2', required: false, desc: '電話番号1-2' },
      { name: 'tel1_3', required: false, desc: '電話番号1-3' },
      { name: 'tel2', required: false, desc: '電話番号2' },
      { name: 'tel2_1', required: false, desc: '電話番号2-1' },
      { name: 'tel2_2', required: false, desc: '電話番号2-2' },
      { name: 'tel2_3', required: false, desc: '電話番号2-3' },
      { name: 'tel3', required: false, desc: '電話番号3' },
      { name: 'tel3_1', required: false, desc: '電話番号3-1' },
      { name: 'tel3_2', required: false, desc: '電話番号3-2' },
      { name: 'tel3_3', required: false, desc: '電話番号3-3' },
      { name: 'fax', required: false, desc: 'FAX' },
      { name: 'fax_1', required: false, desc: 'FAX1' },
      { name: 'fax_2', required: false, desc: 'FAX2' },
      { name: 'fax_3', required: false, desc: 'FAX3' },
      { name: 'sex', required: false, desc: '性別' },
      { name: 'birth', required: false, desc: '生年月日' },
      { name: 'birth_year', required: false, desc: '生年月日（年）' },
      { name: 'birth_month', required: false, desc: '生年月日（月）' },
      { name: 'birth_day', required: false, desc: '生年月日（日）' },
      { name: 'company_name', required: false, desc: '企業名' },
      { name: 'company_post_name', required: false, desc: '部署名' },
      { name: 'company_exective_name', required: false, desc: '役職名' },
      { name: 'advcode_first', required: false, desc: '初回広告コード' },
      { name: 'attribute1', required: false, desc: '属性1' },
      { name: 'attribute2', required: false, desc: '属性2' },
      { name: 'attribute3', required: false, desc: '属性3' },
      { name: 'attribute4', required: false, desc: '属性4' },
      { name: 'attribute5', required: false, desc: '属性5' },
      { name: 'attribute6', required: false, desc: '属性6' },
      { name: 'attribute7', required: false, desc: '属性7' },
      { name: 'attribute8', required: false, desc: '属性8' },
      { name: 'attribute9', required: false, desc: '属性9' },
      { name: 'attribute10', required: false, desc: '属性10' },
      { name: 'login_id', required: false, desc: 'ログインID' },
      { name: 'password', required: false, desc: 'パスワード' },
      { name: 'question', required: false, desc: '質問' },
      { name: 'answer', required: false, desc: '回答' },
      { name: 'remote_addr', required: false, desc: 'リモートIPアドレス' },
      { name: 'mail_flg', required: false, desc: 'メール配信フラグ' },
      { name: 'user_memo', required: false, desc: 'ユーザメモ' },
      { name: 'del_flg', required: false, desc: '削除フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'member_rank_id', required: false, desc: '会員ランクID' },
      { name: 'recommend_uid', required: false, desc: '外部レコメンド連携用ユーザID' },
      { name: 'date_last_loggedin', required: false, desc: '最終ログイン日時' },
      { name: 'user_management_level_id', required: false, desc: 'ユーザー管理レベルID' },
      { name: 'integrated_flg', required: false, desc: 'ユーザー統合フラグ' },
      { name: 'easy_register_flg', required: false, desc: 'かんたん会員フラグ' },
      { name: 'fixed_purchase_member_flg', required: false, desc: '定期会員フラグ' },
      { name: 'access_country_iso_code', required: false, desc: 'アクセス国ISOコード' },
      { name: 'disp_language_code', required: false, desc: '表示言語コード' },
      { name: 'disp_language_locale_id', required: false, desc: '表示言語ロケールID' },
      { name: 'disp_currency_code', required: false, desc: '表示通貨コード' },
      { name: 'disp_currency_locale_id', required: false, desc: '表示通貨ロケールID' },
      { name: 'last_birthday_point_add_year', required: false, desc: '最終誕生日ポイント付与年' },
      { name: 'addr_country_iso_code', required: false, desc: '住所国ISOコード' },
      { name: 'addr_country_name', required: false, desc: '住所国名' },
      {
        name: 'last_birthday_coupon_publish_year',
        required: false,
        desc: '最終誕生日クーポン付与年'
      },
      {
        name: 'order_count_order_realtime',
        required: false,
        desc: 'リアルタイム購入回数（注文基準）'
      },
      { name: 'order_count_old', required: false, desc: '過去累計購入回数' },
      { name: 'referral_code', required: false, desc: '紹介コード' },
      { name: 'referred_user_id', required: false, desc: '紹介元ユーザーID' },
      { name: 'real_shop_id', required: false, desc: 'リアル店舗ID' },
      {
        name: 'order_count_realshop_realtime',
        required: false,
        desc: 'リアルタイム購入回数（リアル店舗）'
      },
      { name: 'date_withdrawal', required: false, desc: '退会日' }
    ],
    sample:
      'user_id,user_kbn,mall_id,name,name_kana,mail_addr,tel1,sex,member_rank_id,login_id,del_flg\nUSR0000001,PC_USER,OWN_SITE,山田太郎,やまだたろう,yamada@example.com,03-1234-5678,MALE,gold,yamada,0'
  },
  {
    type: 'w2_UserAttribute',
    filePrefix: 'w2_user_attr_',
    description: 'W2ユーザー属性（全24項目）を登録・更新します。user_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'user_id', required: true, desc: 'ユーザーID（PK）' },
      { name: 'first_order_date', required: false, desc: '初回購入日' },
      { name: 'second_order_date', required: false, desc: '2回目購入日' },
      { name: 'last_order_date', required: false, desc: '最終購入日' },
      { name: 'enrollment_days', required: false, desc: '在籍期間(日)' },
      { name: 'away_days', required: false, desc: '離脱期間(日)' },
      { name: 'order_amount_order_all', required: false, desc: '累計購入金額（全体）' },
      { name: 'order_amount_order_fp', required: false, desc: '累計購入金額（定期のみ）' },
      { name: 'order_count_order_all', required: false, desc: '累計購入回数（全体）' },
      { name: 'order_count_order_fp', required: false, desc: '累計購入回数（定期のみ）' },
      { name: 'order_amount_ship_all', required: false, desc: '累計購入金額（全体）' },
      { name: 'order_amount_ship_fp', required: false, desc: '累計購入金額（定期のみ）' },
      { name: 'order_count_ship_all', required: false, desc: '累計購入回数（全体）' },
      { name: 'order_count_ship_fp', required: false, desc: '累計購入回数（定期のみ）' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'cpm_cluster_attribute1', required: false, desc: 'CPMクラスタ属性1' },
      { name: 'cpm_cluster_attribute2', required: false, desc: 'CPMクラスタ属性2' },
      { name: 'cpm_cluster_id', required: false, desc: 'CPMクラスタID（計算列）' },
      { name: 'cpm_cluster_attribute1_before', required: false, desc: '以前のCPMクラスタ属性1' },
      { name: 'cpm_cluster_attribute2_before', required: false, desc: '以前のCPMクラスタ属性2' },
      { name: 'cpm_cluster_id_before', required: false, desc: '以前のCPMクラスタID（計算列）' },
      { name: 'cpm_cluster_changed_date', required: false, desc: 'CPMクラスタ変更日' }
    ],
    sample:
      'user_id,first_order_date,last_order_date,order_amount_order_all,order_count_order_all,cpm_cluster_attribute1,cpm_cluster_attribute2,cpm_cluster_id\nUSR0000001,2025-01-15,2026-03-10,150000,12,STDY_,ACT,STDY_ACT'
  },
  {
    type: 'w2_MemberRank',
    filePrefix: 'w2_member_rank_',
    description: 'W2会員ランクマスタ（全17項目）を登録・更新します。member_rank_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'member_rank_id', required: true, desc: 'ランクID（PK）' },
      { name: 'member_rank_order', required: false, desc: 'ランク順位' },
      { name: 'member_rank_name', required: false, desc: 'ランク名' },
      { name: 'order_discount_type', required: false, desc: '注文割引方法' },
      { name: 'order_discount_value', required: false, desc: '注文割引数' },
      { name: 'order_discount_threshold_price', required: false, desc: '注文金額割引き閾値' },
      { name: 'point_add_type', required: false, desc: 'ポイント加算方法' },
      { name: 'point_add_value', required: false, desc: 'ポイント加算数' },
      { name: 'shipping_discount_type', required: false, desc: '配送料割引方法' },
      { name: 'shipping_discount_value', required: false, desc: '配送料割引数' },
      {
        name: 'default_member_rank_setting_flg',
        required: false,
        desc: 'デフォルト会員ランク設定フラグ'
      },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'member_rank_memo', required: false, desc: '会員ランクメモ' },
      { name: 'fixed_purchase_discount_rate', required: false, desc: '定期会員割引率' }
    ],
    sample:
      'member_rank_id,member_rank_order,member_rank_name,order_discount_type,order_discount_value,valid_flg\nRANK_GOLD,1,ゴールド会員,RATE,10,ON\nRANK_NORMAL,999,一般会員,NONE,,ON'
  },
  {
    type: 'w2_AccessLog',
    filePrefix: 'w2_access_log_',
    description: 'W2アクセスログ（全66項目）を登録・更新します。log_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'log_no', required: true, desc: 'ログNo（PK）' },
      { name: 'access_date', required: false, desc: 'アクセスユーザーID' },
      { name: 'access_time', required: false, desc: 'ユーザーID' },
      { name: 'client_ip', required: false, desc: 'クライアントIPアドレス' },
      { name: 'server_name', required: false, desc: 'サーバー名' },
      { name: 'server_ip', required: false, desc: 'サーバーIPアドレス' },
      { name: 'server_port', required: false, desc: 'サーバーポート' },
      { name: 'protocol_status', required: false, desc: 'プロトコルの状態' },
      { name: 'user_agent', required: false, desc: 'ユーザーエージェント' },
      { name: 'url_domain', required: false, desc: 'ドメイン' },
      { name: 'url_page', required: false, desc: 'ページパス' },
      { name: 'url_param', required: false, desc: 'パラメーター' },
      { name: 'dept_id', required: false, desc: '識別ID' },
      { name: 'account_id', required: false, desc: 'アカウントID' },
      { name: 'access_user_id', required: false, desc: 'アクセスユーザーID' },
      { name: 'session_id', required: false, desc: 'セッションID' },
      { name: 'real_user_id', required: false, desc: '認知ユーザーID' },
      { name: 'access_interval', required: false, desc: 'アクセス頻度' },
      { name: 'first_login_flg', required: false, desc: '初回ログインフラグ' },
      { name: 'referrer_domain', required: false, desc: 'リファラー・ドメイン' },
      { name: 'referrer_page', required: false, desc: 'リファラー・ページパス' },
      { name: 'referrer_param', required: false, desc: 'リファラー・パラメーター' },
      { name: 'search_engine', required: false, desc: '検索エンジン' },
      { name: 'search_words', required: false, desc: '検索ワード' },
      { name: 'action_kbn', required: false, desc: 'アクション区分' },
      { name: 's1', required: false, desc: 'S1' },
      { name: 's2', required: false, desc: 'S2' },
      { name: 's3', required: false, desc: 'S3' },
      { name: 's4', required: false, desc: 'S4' },
      { name: 's5', required: false, desc: 'S5' },
      { name: 's6', required: false, desc: 'S6' },
      { name: 's7', required: false, desc: 'S7' },
      { name: 's8', required: false, desc: 'S8' },
      { name: 's9', required: false, desc: 'S9' },
      { name: 's10', required: false, desc: 'S10' },
      { name: 's11', required: false, desc: 'S11' },
      { name: 's12', required: false, desc: 'S12' },
      { name: 's13', required: false, desc: 'S13' },
      { name: 's14', required: false, desc: 'S14' },
      { name: 's15', required: false, desc: 'S15' },
      { name: 's16', required: false, desc: 'S16' },
      { name: 's17', required: false, desc: 'S17' },
      { name: 's18', required: false, desc: 'S18' },
      { name: 's19', required: false, desc: 'S19' },
      { name: 's20', required: false, desc: 'S20' },
      { name: 'p1', required: false, desc: 'P1' },
      { name: 'p2', required: false, desc: 'P2' },
      { name: 'p3', required: false, desc: 'P3' },
      { name: 'p4', required: false, desc: 'P4' },
      { name: 'p5', required: false, desc: 'P5' },
      { name: 'p6', required: false, desc: 'P6' },
      { name: 'p7', required: false, desc: 'P7' },
      { name: 'p8', required: false, desc: 'P8' },
      { name: 'p9', required: false, desc: 'P9' },
      { name: 'p10', required: false, desc: 'P10' },
      { name: 'p11', required: false, desc: 'P11' },
      { name: 'p12', required: false, desc: 'P12' },
      { name: 'p13', required: false, desc: 'P13' },
      { name: 'p14', required: false, desc: 'P14' },
      { name: 'p15', required: false, desc: 'P15' },
      { name: 'p16', required: false, desc: 'P16' },
      { name: 'p17', required: false, desc: 'P17' },
      { name: 'p18', required: false, desc: 'P18' },
      { name: 'p19', required: false, desc: 'P19' },
      { name: 'p20', required: false, desc: 'P20' },
      { name: 'user_agent_kbn', required: false, desc: 'ユーザーエージェント区分' }
    ],
    sample:
      'log_no,access_date,access_time,real_user_id,url_domain,url_page,action_kbn,client_ip,user_agent_kbn,protocol_status\n1,2026-03-28,14:30:25,USR0000001,example.com,/products/detail,VIEW,192.168.1.1,PC,200'
  },
  {
    type: 'w2_Product',
    filePrefix: 'w2_product_',
    description: 'W2商品マスタ（全166項目）を登録・更新します。shop_id+product_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID（PK1）' },
      { name: 'supplier_id', required: false, desc: 'サプライヤID' },
      { name: 'product_id', required: true, desc: '商品ID（PK2）' },
      { name: 'cooperation_id1', required: false, desc: '商品連携ID1' },
      { name: 'cooperation_id2', required: false, desc: '商品連携ID2' },
      { name: 'cooperation_id3', required: false, desc: '商品連携ID3' },
      { name: 'cooperation_id4', required: false, desc: '商品連携ID4' },
      { name: 'cooperation_id5', required: false, desc: '商品連携ID5' },
      { name: 'mall_ex_product_id', required: false, desc: 'モール拡張商品ID' },
      { name: 'maker_id', required: false, desc: 'メーカーID' },
      { name: 'maker_name', required: false, desc: 'メーカー名' },
      { name: 'name', required: false, desc: '商品名' },
      { name: 'name_kana', required: false, desc: '商品名かな' },
      { name: 'name2', required: false, desc: '商品名2' },
      { name: 'name2_kana', required: false, desc: '商品名2かな' },
      { name: 'catchcopy', required: false, desc: 'キャッチコピー' },
      { name: 'outline_kbn', required: false, desc: '商品概要HTML区分' },
      { name: 'outline', required: false, desc: '商品概要' },
      { name: 'desc_detail_kbn1', required: false, desc: '商品詳細1 HTML区分' },
      { name: 'desc_detail1', required: false, desc: '商品詳細1' },
      { name: 'desc_detail_kbn2', required: false, desc: '商品詳細2 HTML区分' },
      { name: 'desc_detail2', required: false, desc: '商品詳細2' },
      { name: 'desc_detail_kbn3', required: false, desc: '商品詳細3 HTML区分' },
      { name: 'desc_detail3', required: false, desc: '商品詳細3' },
      { name: 'desc_detail_kbn4', required: false, desc: '商品詳細4 HTML区分' },
      { name: 'desc_detail4', required: false, desc: '商品詳細4' },
      { name: 'return_exchange_message', required: false, desc: '返品交換文言' },
      { name: 'note', required: false, desc: '備考' },
      { name: 'display_price', required: false, desc: '商品表示価格' },
      { name: 'display_special_price', required: false, desc: '商品表示特別価格' },
      { name: 'tax_included_flg', required: false, desc: '税込フラグ' },
      { name: 'tax_rate', required: false, desc: '税率(%)' },
      { name: 'tax_round_type', required: false, desc: '税計算方法' },
      { name: 'price_pretax', required: false, desc: '税込販売価格' },
      { name: 'price_shipping', required: false, desc: '配送料' },
      { name: 'shipping_type', required: false, desc: '配送料種別' },
      { name: 'shipping_size_kbn', required: false, desc: '配送サイズ区分' },
      { name: 'price_cost', required: false, desc: '商品原価' },
      { name: 'point_kbn1', required: false, desc: '付与ポイント区分1' },
      { name: 'point1', required: false, desc: '付与ポイント1' },
      { name: 'point_kbn2', required: false, desc: '付与ポイント区分2（定期用）' },
      { name: 'point2', required: false, desc: '付与ポイント2（定期用）' },
      { name: 'point_kbn3', required: false, desc: '付与ポイント区分3' },
      { name: 'point3', required: false, desc: '付与ポイント3' },
      { name: 'campaign_from', required: false, desc: 'キャンペーン期間(FROM)' },
      { name: 'campaign_to', required: false, desc: 'キャンペーン期間(TO)' },
      { name: 'campaign_point_kbn', required: false, desc: 'キャンペーン付与ポイント区分' },
      { name: 'campaign_point', required: false, desc: 'キャンペーン付与ポイント' },
      { name: 'display_from', required: false, desc: '表示期間(FROM)' },
      { name: 'display_to', required: false, desc: '表示期間(TO)' },
      { name: 'sell_from', required: false, desc: '販売期間(FROM)' },
      { name: 'sell_to', required: false, desc: '販売期間(TO)' },
      { name: 'before_sale_display_kbn', required: false, desc: '販売期間前表示フラグ' },
      { name: 'after_sale_display_kbn', required: false, desc: '販売期間後表示フラグ' },
      { name: 'max_sell_quantity', required: false, desc: '販売可能数' },
      { name: 'stock_management_kbn', required: false, desc: '在庫管理方法' },
      { name: 'stock_disp_kbn', required: false, desc: '在庫表示区分' },
      { name: 'stock_message_id', required: false, desc: '商品在庫文言ID' },
      { name: 'url', required: false, desc: '紹介URL' },
      { name: 'inquire_email', required: false, desc: '問い合わせ用メールアドレス' },
      { name: 'inquire_tel', required: false, desc: '問い合わせ用電話番号' },
      { name: 'display_kbn', required: false, desc: '検索時表示区分' },
      { name: 'category_id1', required: false, desc: 'カテゴリID1' },
      { name: 'category_id2', required: false, desc: 'カテゴリID2' },
      { name: 'category_id3', required: false, desc: 'カテゴリID3' },
      { name: 'category_id4', required: false, desc: 'カテゴリID4' },
      { name: 'category_id5', required: false, desc: 'カテゴリID5' },
      { name: 'related_product_id_cs1', required: false, desc: '関連商品ID1' },
      { name: 'related_product_id_cs2', required: false, desc: '関連商品ID2' },
      { name: 'related_product_id_cs3', required: false, desc: '関連商品ID3' },
      { name: 'related_product_id_cs4', required: false, desc: '関連商品ID4' },
      { name: 'related_product_id_cs5', required: false, desc: '関連商品ID5' },
      { name: 'related_product_id_us1', required: false, desc: '関連商品ID1' },
      { name: 'related_product_id_us2', required: false, desc: '関連商品ID2' },
      { name: 'related_product_id_us3', required: false, desc: '関連商品ID3' },
      { name: 'related_product_id_us4', required: false, desc: '関連商品ID4' },
      { name: 'related_product_id_us5', required: false, desc: '関連商品ID5' },
      { name: 'image_head', required: false, desc: '商品画像名ヘッダ' },
      { name: 'icon_flg1', required: false, desc: 'アイコンフラグ1' },
      { name: 'icon_term_end1', required: false, desc: 'アイコン有効期限1' },
      { name: 'icon_flg2', required: false, desc: 'アイコンフラグ2' },
      { name: 'icon_term_end2', required: false, desc: 'アイコン有効期限2' },
      { name: 'icon_flg3', required: false, desc: 'アイコンフラグ3' },
      { name: 'icon_term_end3', required: false, desc: 'アイコン有効期限3' },
      { name: 'icon_flg4', required: false, desc: 'アイコンフラグ4' },
      { name: 'icon_term_end4', required: false, desc: 'アイコン有効期限4' },
      { name: 'icon_flg5', required: false, desc: 'アイコンフラグ5' },
      { name: 'icon_term_end5', required: false, desc: 'アイコン有効期限5' },
      { name: 'icon_flg6', required: false, desc: 'アイコンフラグ6' },
      { name: 'icon_term_end6', required: false, desc: 'アイコン有効期限6' },
      { name: 'icon_flg7', required: false, desc: 'アイコンフラグ7' },
      { name: 'icon_term_end7', required: false, desc: 'アイコン有効期限7' },
      { name: 'icon_flg8', required: false, desc: 'アイコンフラグ8' },
      { name: 'icon_term_end8', required: false, desc: 'アイコン有効期限8' },
      { name: 'icon_flg9', required: false, desc: 'アイコンフラグ9' },
      { name: 'icon_term_end9', required: false, desc: 'アイコン有効期限9' },
      { name: 'icon_flg10', required: false, desc: 'アイコンフラグ10' },
      { name: 'icon_term_end10', required: false, desc: 'アイコン有効期限10' },
      { name: 'use_variation_flg', required: false, desc: '複数バリエーション使用フラグ' },
      { name: 'reservation_flg', required: false, desc: '予約商品フラグ' },
      { name: 'fixed_purchase_flg', required: false, desc: '定期購入フラグ' },
      { name: 'mall_cooperated_flg', required: false, desc: 'モール連携済フラグ' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'del_flg', required: false, desc: '削除フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'search_keyword', required: false, desc: '検索キーワード' },
      { name: 'member_rank_discount_flg', required: false, desc: '会員ランク割引対象フラグ' },
      { name: 'display_member_rank', required: false, desc: '閲覧可能会員ランク' },
      { name: 'buyable_member_rank', required: false, desc: '購入可能会員ランク' },
      { name: 'google_shopping_flg', required: false, desc: 'Googleショッピング連携フラグ' },
      { name: 'product_option_settings', required: false, desc: '商品付帯情報設定' },
      { name: 'arrival_mail_valid_flg', required: false, desc: '再入荷通知メール有効フラグ' },
      { name: 'release_mail_valid_flg', required: false, desc: '販売開始通知メール有効フラグ' },
      { name: 'resale_mail_valid_flg', required: false, desc: '再販売通知メール有効フラグ' },
      { name: 'select_variation_kbn', required: false, desc: 'PC用商品詳細バリエーション選択方法' },
      { name: 'brand_id1', required: false, desc: 'ブランドID1' },
      { name: 'brand_id2', required: false, desc: 'ブランドID2' },
      { name: 'brand_id3', required: false, desc: 'ブランドID3' },
      { name: 'brand_id4', required: false, desc: 'ブランドID4' },
      { name: 'brand_id5', required: false, desc: 'ブランドID5' },
      { name: 'use_recommend_flg', required: false, desc: '外部レコメンド利用フラグ' },
      { name: 'gift_flg', required: false, desc: 'ギフト購入フラグ' },
      { name: 'age_limit_flg', required: false, desc: '年齢制限フラグ' },
      { name: 'plural_shipping_price_free_flg', required: false, desc: '配送料金複数個無料フラグ' },
      { name: 'digital_contents_flg', required: false, desc: 'デジタルコンテンツ商品フラグ' },
      { name: 'download_url', required: false, desc: 'ダウンロードURL' },
      { name: 'display_sell_flg', required: false, desc: '販売期間表示フラグ' },
      { name: 'display_priority', required: false, desc: '表示優先順' },
      { name: 'limited_payment_ids', required: false, desc: '決済利用不可' },
      { name: 'fixed_purchase_firsttime_price', required: false, desc: '定期初回購入価格' },
      { name: 'fixed_purchase_price', required: false, desc: '定期購入価格' },
      { name: 'bundle_item_display_type', required: false, desc: '同梱商品明細表示フラグ' },
      { name: 'product_type', required: false, desc: '商品区分' },
      {
        name: 'limited_fixed_purchase_kbn1_setting',
        required: false,
        desc: '利用不可定期購入配送周期月'
      },
      {
        name: 'limited_fixed_purchase_kbn3_setting',
        required: false,
        desc: '利用不可定期購入配送周期日'
      },
      { name: 'recommend_product_id', required: false, desc: 'レコメンド連携用商品ID' },
      { name: 'cooperation_id6', required: false, desc: '商品連携ID6' },
      { name: 'cooperation_id7', required: false, desc: '商品連携ID7' },
      { name: 'cooperation_id8', required: false, desc: '商品連携ID8' },
      { name: 'cooperation_id9', required: false, desc: '商品連携ID9' },
      { name: 'cooperation_id10', required: false, desc: '商品連携ID10' },
      {
        name: 'fixed_purchase_product_order_limit_flg',
        required: false,
        desc: '定期購入初回購入チェックフラグ'
      },
      { name: 'andmall_reservation_flg', required: false, desc: '&mallの予約商品フラグ' },
      { name: 'product_color_id', required: false, desc: '商品カラーID' },
      {
        name: 'display_only_fixed_purchase_member_flg',
        required: false,
        desc: '定期会員限定フラグ（閲覧）'
      },
      {
        name: 'sell_only_fixed_purchase_member_flg',
        required: false,
        desc: '定期会員限定フラグ（販売）'
      },
      { name: 'product_weight_gram', required: false, desc: '商品重量(g)' },
      { name: 'tax_category_id', required: false, desc: '税率カテゴリID' },
      {
        name: 'product_order_limit_flg_fp',
        required: false,
        desc: '通常購入初回購入チェックフラグ'
      },
      { name: 'fixed_purchase_cancelable_count', required: false, desc: '定期解約可能回数' },
      {
        name: 'fixed_purchase_limited_user_level_ids',
        required: false,
        desc: '定期購入制限ユーザレベル'
      },
      {
        name: 'fixed_purchase_next_shipping_product_id',
        required: false,
        desc: '定期2回目以降配送商品ID'
      },
      {
        name: 'fixed_purchase_next_shipping_variation_id',
        required: false,
        desc: '定期2回目以降配送バリエーションID'
      },
      {
        name: 'fixed_purchase_next_shipping_item_quantity',
        required: false,
        desc: '定期2回目以降配送注文個数'
      },
      {
        name: 'fixed_purchase_limited_skipped_count',
        required: false,
        desc: '定期購入スキップ制限回数'
      },
      {
        name: 'next_shipping_item_fixed_purchase_kbn',
        required: false,
        desc: '定期2回目以降配送 定期購入区分'
      },
      {
        name: 'next_shipping_item_fixed_purchase_setting',
        required: false,
        desc: '定期2回目以降配送 定期購入設定'
      },
      { name: 'product_size_factor', required: false, desc: '商品サイズ係数' },
      { name: 'add_cart_url_limit_flg', required: false, desc: 'カート投入URL制限フラグ' },
      {
        name: 'limited_fixed_purchase_kbn4_setting',
        required: false,
        desc: '利用不可定期購入配送周期週'
      },
      { name: 'medicine_classification', required: false, desc: '医薬品分類項目' },
      { name: 'pos_cooperation_id', required: false, desc: 'POS連携ID' },
      { name: 'temperature_zones', required: false, desc: '配送可能温度帯' },
      { name: 'slug', required: false, desc: 'スラッグ' }
    ],
    sample:
      'shop_id,product_id,name,name_kana,display_price,tax_included_flg,tax_rate,category_id1,brand_id1,sell_from,display_kbn,valid_flg\nSHOP1,PRD001,Tシャツ 白,ティーシャツシロ,2980,1,10,CAT001,BRAND001,2026-01-01,0,1'
  },
  {
    type: 'w2_ProductLink',
    filePrefix: 'w2_product_link_',
    description: 'W2商品リンクマスタ（全4項目）を登録・更新します。shop_id+product_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID（PK1）' },
      { name: 'product_id', required: true, desc: '商品ID（PK2）' },
      { name: 'image_url', required: false, desc: '商品画像URL' },
      { name: 'product_url', required: false, desc: '商品ページURL' }
    ],
    sample:
      'shop_id,product_id,image_url,product_url\nSHOP1,PRD001,https://example.com/img/prd001.jpg,https://example.com/products/prd001'
  },
  // 拡張マスタ（add-w2-ex-product-spec で追加）。W2 の全件出力に合わせて全件置き換えで取り込む（design.md D3）
  {
    type: 'w2_ExProductSpec',
    filePrefix: 'w2_ex_product_spec_',
    description:
      '商品追加情報（全8項目。W2: kpp_ProductSpec）を登録・更新します。shop_id+product_id+spec_no+spec_typeでUPSERTし、全件置き換えで取り込みます。' +
      '【全件連携】W2から毎回全件を1ファイルで連携してください。FTP自動取込・ファイル取込では、ファイルに含まれない既存の行を削除します（ファイルを分けて送ると、後に取り込んだファイルに無い行が消えます）。' +
      'エラー行を含むファイル・0行のファイルでは削除せず、理由を取込実行ログのエラー詳細に出します。削除した行数は取込実行ログの「削除」に表示されます。マスタ一覧画面の一括登録・単件の登録/編集では削除しません。' +
      '【使われ方】商品バリエーション追加情報と合わせて商品単位にまとめ（同じスペック種別の値は重複を除く）、Findeeの検索・商品意味理解・似た商品・検索タグの候補に使います。値が空の行は取り込みますが、これらには使いません。' +
      '【反映】検索は次のベクトル同期、商品意味理解と検索タグの候補は翌朝の日次処理、似た商品は次の再学習で反映されます。',
    behavior: 'UPSERT＋全件置き換え（ファイルに無い行を削除）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID（PK1）' },
      { name: 'product_id', required: true, desc: '商品ID（PK2）。w2_Product.product_id と対応' },
      {
        name: 'spec_no',
        required: true,
        desc: '枝番（PK3）。同じスペック種別に複数の値を持つときに区別する'
      },
      {
        name: 'spec_type',
        required: true,
        desc: 'スペック種別（PK4）。例：カラー・素材（サイトごとに異なる）'
      },
      { name: 'spec_value', required: true, desc: '値' },
      { name: 'date_created', required: false, desc: '登録日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'shop_id,product_id,spec_no,spec_type,spec_value,date_created,date_changed,last_changed\nSHOP1,PRD001,1,カラー,レッド,2026/09/01 10:00:00,2026/09/01 10:00:00,operator\nSHOP1,PRD001,2,カラー,ブルー,2026/09/01 10:00:00,2026/09/01 10:00:00,operator\nSHOP1,PRD001,1,素材,コットン,2026/09/01 10:00:00,2026/09/01 10:00:00,operator'
  },
  {
    type: 'w2_ExProductVariationSpec',
    filePrefix: 'w2_ex_product_variation_spec_',
    description:
      '商品バリエーション追加情報（全9項目。W2: kpp_ProductVariationSpec）を登録・更新します。shop_id+product_id+variation_id+spec_no+spec_typeでUPSERTし、全件置き換えで取り込みます。' +
      '【全件連携】W2から毎回全件を1ファイルで連携してください。FTP自動取込・ファイル取込では、ファイルに含まれない既存の行を削除します（ファイルを分けて送ると、後に取り込んだファイルに無い行が消えます）。' +
      'エラー行を含むファイル・0行のファイルでは削除せず、理由を取込実行ログのエラー詳細に出します。削除した行数は取込実行ログの「削除」に表示されます。マスタ一覧画面の一括登録・単件の登録/編集では削除しません。' +
      '【使われ方】バリエーションのスペックは商品追加情報と合わせて商品単位にまとめ（同じスペック種別の値は重複を除く）、Findeeの検索・商品意味理解・似た商品・検索タグの候補に使います。値が空の行は取り込みますが、これらには使いません。' +
      '【反映】検索は次のベクトル同期、商品意味理解と検索タグの候補は翌朝の日次処理、似た商品は次の再学習で反映されます。',
    behavior: 'UPSERT＋全件置き換え（ファイルに無い行を削除）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID（PK1）' },
      { name: 'product_id', required: true, desc: '商品ID（PK2）。w2_Product.product_id と対応' },
      {
        name: 'variation_id',
        required: true,
        desc: 'バリエーションID（PK3）。w2_ProductVariation.variation_id と対応'
      },
      {
        name: 'spec_no',
        required: true,
        desc: '枝番（PK4）。同じスペック種別に複数の値を持つときに区別する'
      },
      {
        name: 'spec_type',
        required: true,
        desc: 'スペック種別（PK5）。例：カラー・サイズ（サイトごとに異なる）'
      },
      { name: 'spec_value', required: true, desc: '値' },
      { name: 'date_created', required: false, desc: '登録日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'shop_id,product_id,variation_id,spec_no,spec_type,spec_value,date_created,date_changed,last_changed\nSHOP1,PRD001,VAR001,1,カラー,レッド,2026/09/01 10:00:00,2026/09/01 10:00:00,operator\nSHOP1,PRD001,VAR001,1,サイズ,M,2026/09/01 10:00:00,2026/09/01 10:00:00,operator'
  },
  {
    type: 'w2_ProductBrand',
    filePrefix: 'w2_product_brand_',
    description: 'W2商品ブランド（全10項目）を登録・更新します。brand_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'brand_id', required: true, desc: 'ブランドID（PK）' },
      { name: 'brand_name', required: false, desc: 'ブランド名' },
      { name: 'brand_title', required: false, desc: 'ブランドタイトル（SEO表示用）' },
      { name: 'additional_design_tag', required: false, desc: '追加デザインタグ' },
      { name: 'seo_keyword', required: false, desc: 'SEOキーワード' },
      { name: 'default_flg', required: false, desc: 'デフォルト設定フラグ' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'brand_id,brand_name,brand_title,seo_keyword,default_flg,valid_flg\nBRAND001,テストブランド,テストブランド公式,テスト ブランド,OFF,ON'
  },
  {
    type: 'w2_ProductCategory',
    filePrefix: 'w2_product_category_',
    description: 'W2商品カテゴリ（全20項目）を登録・更新します。shop_id+category_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID（PK1）' },
      { name: 'category_id', required: true, desc: 'カテゴリID（PK2）' },
      { name: 'parent_category_id', required: false, desc: '親カテゴリID' },
      { name: 'name', required: false, desc: 'カテゴリ名' },
      { name: 'url', required: false, desc: 'カテゴリページURL' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'del_flg', required: false, desc: '削除フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'permitted_brand_ids', required: false, desc: '許可ブランドID' },
      { name: 'use_recommend_flg', required: false, desc: '外部レコメンド利用フラグ' },
      { name: 'member_rank_id', required: false, desc: '閲覧可能会員ランクID' },
      {
        name: 'lower_member_can_display_tree_flg',
        required: false,
        desc: '会員ランク表示制御：カテゴリツリー表示フラグ'
      },
      { name: 'name_kana', required: false, desc: 'カテゴリカナ名' },
      { name: 'display_order', required: false, desc: '表示順' },
      { name: 'child_category_sort_kbn', required: false, desc: '子カテゴリの並び順' },
      { name: 'only_fixed_purchase_member_flg', required: false, desc: '定期会員限定フラグ' },
      { name: 'canonical_text', required: false, desc: 'カノニカルタグ用テキスト' },
      { name: 'slug', required: false, desc: 'スラッグ' }
    ],
    sample:
      'shop_id,category_id,parent_category_id,name,display_order,valid_flg,del_flg\nSHOP1,CAT001,,アパレル,1,ON,0\nSHOP1,CAT002,CAT001,トップス,2,ON,0'
  },
  {
    type: 'w2_ProductTag',
    filePrefix: 'w2_product_tag_',
    description: 'W2商品タグ（全4項目）を登録・更新します。product_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'product_id', required: true, desc: '商品ID（PK）' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample: 'product_id,date_created,date_changed,last_changed\nPRD001,2026-03-01,2026-03-01,admin'
  },
  // 商品タグ設定マスター（add-w2-product-tag-setting で追加）
  {
    type: 'w2_ProductTagSetting',
    filePrefix: 'w2_product_tag_setting_',
    description:
      '商品タグ設定（全8項目。W2: w2_ProductTagSetting）を登録・更新します。tag_noでUPSERTし、全件置き換えで取り込みます。' +
      '【役割】W2の商品タグ（w2_ProductTag）は列が固定ではなく、ここで作ったタグIDがそのまま列名になり、値は商品ごとに設定した値が入ります。' +
      'そのため、このファイルが無いとタグIDが何のタグなのか（タイトル・有効/無効）が分かりません。商品タグのファイルと合わせて連携してください。' +
      '【全件連携】W2から毎回全件を1ファイルで連携してください。FTP自動取込・ファイル取込では、ファイルに含まれない既存の行を削除します（ファイルを分けて送ると、後に取り込んだファイルに無い行が消えます）。' +
      'エラー行を含むファイル・0行のファイルでは削除せず、理由を取込実行ログのエラー詳細に出します。削除した行数は取込実行ログの「削除」に表示されます。マスタ一覧画面の一括登録・単件の登録/編集では削除しません。' +
      '【使われ方】Findeeの検索・似た商品では「タイトル: 値」の形で商品のデータに載せ、UGCレビュー配信の商品タグ条件の候補にも使います。' +
      '有効フラグが0のタグはこれらに使いません（一覧・詳細には表示されます）。' +
      '【反映】検索は次のベクトル同期、似た商品は次の再学習で反映されます。',
    behavior: 'UPSERT＋全件置き換え（ファイルに無い行を削除）',
    columns: [
      { name: 'tag_no', required: true, desc: 'タグNo（PK）' },
      {
        name: 'tag_id',
        required: false,
        desc: 'タグID。w2_ProductTagの列名になる（例：title / artist / label）'
      },
      { name: 'tag_name', required: false, desc: 'タイトル（画面・検索で使う表示名）' },
      { name: 'tag_discription', required: false, desc: '説明' },
      { name: 'tag_valid_flg', required: false, desc: '有効フラグ（有効：1 / 無効：0。空は有効扱い）' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'tag_no,tag_id,tag_name,tag_discription,tag_valid_flg,date_created,date_changed,last_changed\n1,artist,アーティスト,アーティスト名,1,2026-03-01,2026-03-01,admin\n2,label,レーベル,レーベル名,1,2026-03-01,2026-03-01,admin'
  },
  {
    type: 'w2_ProductStock',
    filePrefix: 'w2_product_stock_',
    description:
      'W2商品在庫（全12項目）を登録・更新します。shop_id+product_id+variation_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID（PK1）' },
      { name: 'product_id', required: true, desc: '商品ID（PK2）' },
      { name: 'variation_id', required: true, desc: '商品バリエーションID（PK3）' },
      { name: 'stock', required: false, desc: '商品在庫数' },
      { name: 'stock_alert', required: false, desc: '商品在庫安全基準' },
      { name: 'realstock', required: false, desc: '実在庫数A' },
      { name: 'realstock_b', required: false, desc: '実在庫数B' },
      { name: 'realstock_c', required: false, desc: '実在庫数C' },
      { name: 'realstock_reserved', required: false, desc: '引当済実在庫数' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'shop_id,product_id,variation_id,stock,stock_alert,realstock,realstock_reserved\nSHOP1,PRD001,VAR001,100,10,120,20'
  },
  {
    type: 'w2_ProductVariation',
    filePrefix: 'w2_product_variation_',
    description:
      'W2商品バリエーション（全39項目）を登録・更新します。shop_id+product_id+variation_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID（PK1）' },
      { name: 'product_id', required: true, desc: '商品ID（PK2）' },
      { name: 'variation_id', required: true, desc: '商品バリエーションID（PK3）' },
      { name: 'variation_cooperation_id1', required: false, desc: '商品バリエーション連携ID1' },
      { name: 'variation_cooperation_id2', required: false, desc: '商品バリエーション連携ID2' },
      { name: 'variation_cooperation_id3', required: false, desc: '商品バリエーション連携ID3' },
      { name: 'variation_cooperation_id4', required: false, desc: '商品バリエーション連携ID4' },
      { name: 'variation_cooperation_id5', required: false, desc: '商品バリエーション連携ID5' },
      { name: 'mall_variation_id1', required: false, desc: 'モールバリエーションID1' },
      { name: 'mall_variation_id2', required: false, desc: 'モールバリエーションID2' },
      { name: 'mall_variation_type', required: false, desc: 'モールバリエーション種別' },
      { name: 'variation_name1', required: false, desc: 'バリエーション名1' },
      { name: 'variation_name2', required: false, desc: 'バリエーション名2' },
      { name: 'variation_name3', required: false, desc: 'バリエーション名3' },
      { name: 'price', required: false, desc: '販売価格' },
      { name: 'special_price', required: false, desc: '特別価格' },
      { name: 'variation_image_head', required: false, desc: 'バリエーション画像名ヘッダ' },
      { name: 'shipping_type', required: false, desc: '配送料種別' },
      { name: 'shipping_size_kbn', required: false, desc: '配送サイズ区分' },
      { name: 'display_order', required: false, desc: '表示順' },
      {
        name: 'variation_mall_cooperated_flg',
        required: false,
        desc: 'バリエーションモール連携済フラグ'
      },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'del_flg', required: false, desc: '削除フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'variation_download_url', required: false, desc: 'ダウンロードURL' },
      {
        name: 'variation_fixed_purchase_firsttime_price',
        required: false,
        desc: '定期初回購入価格'
      },
      { name: 'variation_fixed_purchase_price', required: false, desc: '定期購入価格' },
      { name: 'variation_cooperation_id6', required: false, desc: '商品バリエーション連携ID6' },
      { name: 'variation_cooperation_id7', required: false, desc: '商品バリエーション連携ID7' },
      { name: 'variation_cooperation_id8', required: false, desc: '商品バリエーション連携ID8' },
      { name: 'variation_cooperation_id9', required: false, desc: '商品バリエーション連携ID9' },
      { name: 'variation_cooperation_id10', required: false, desc: '商品バリエーション連携ID10' },
      { name: 'variation_color_id', required: false, desc: '商品カラーID' },
      { name: 'variation_andmall_reservation_flg', required: false, desc: '&mallの予約商品フラグ' },
      { name: 'variation_weight_gram', required: false, desc: '商品バリエーション重量（g）' },
      {
        name: 'variation_add_cart_url_limit_flg',
        required: false,
        desc: 'カート投入URL制限フラグ'
      },
      { name: 'variation_pos_cooperation_id', required: false, desc: 'POS連携ID' }
    ],
    sample:
      'shop_id,product_id,variation_id,variation_name1,variation_name2,price,display_order\nSHOP1,PRD001,VAR001,L,\u8d64,1000,1'
  },
  {
    type: 'w2_ProductPrice',
    filePrefix: 'w2_product_price_',
    description:
      'W2商品価格（全8項目）を登録・更新します。バリエーション×会員ランクの会員価格。shop_id+product_id+variation_id+member_rank_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID（PK1）' },
      { name: 'product_id', required: true, desc: '商品ID（PK2）' },
      { name: 'variation_id', required: true, desc: '商品バリエーションID（PK3）' },
      { name: 'member_rank_id', required: true, desc: '会員ランクID（PK4）' },
      { name: 'member_rank_price', required: false, desc: '会員価格' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'shop_id,product_id,variation_id,member_rank_id,member_rank_price\nSHOP1,PRD001,VAR001,GOLD,900'
  },
  {
    type: 'w2_Cart',
    filePrefix: 'w2_cart_',
    description: 'W2カート（全26項目）を登録・更新します。cart_id+cart_item_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'cart_id', required: true, desc: 'カートID（PK1）' },
      { name: 'cart_item_no', required: true, desc: '枝番（PK2）' },
      { name: 'user_id', required: false, desc: 'ユーザID' },
      { name: 'shop_id', required: false, desc: '店舗ID' },
      { name: 'supplier_id', required: false, desc: 'サプライヤID' },
      { name: 'product_id', required: false, desc: '商品ID' },
      { name: 'variation_id', required: false, desc: '商品バリエーションID' },
      { name: 'product_count', required: false, desc: '商品注文数' },
      { name: 'product_set_id', required: false, desc: '商品セットID' },
      { name: 'product_set_no', required: false, desc: '商品セット枝番' },
      { name: 'product_set_count', required: false, desc: '商品セット注文数' },
      { name: 'cart_div_type1', required: false, desc: 'カート分割基準1（配送種別）' },
      { name: 'cart_div_type2', required: false, desc: 'カート分割基準2（デジタルコンテンツ）' },
      { name: 'cart_div_type3', required: false, desc: 'カート分割基準3' },
      { name: 'kbn1', required: false, desc: '区分1' },
      { name: 'kbn2', required: false, desc: '区分2' },
      { name: 'kbn3', required: false, desc: '区分3' },
      { name: 'productsale_id', required: false, desc: '商品セールID' },
      { name: 'fixed_purchase_flg', required: false, desc: '定期購入フラグ' },
      { name: 'del_flg', required: false, desc: '削除フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'product_option_texts', required: false, desc: '商品付帯情報選択値' },
      { name: 'gift_order_flg', required: false, desc: 'ギフト購入フラグ' },
      { name: 'novelty_id', required: false, desc: 'ノベルティID' },
      { name: 'recommend_id', required: false, desc: 'レコメンドID' }
    ],
    sample:
      'cart_id,cart_item_no,user_id,shop_id,product_id,variation_id,product_count,fixed_purchase_flg,del_flg\nCART001,1,USR0000001,SHOP1,PRD001,VAR001,2,0,0'
  },
  {
    type: 'w2_Order',
    filePrefix: 'w2_order_',
    description: 'W2受注マスタ（全248項目）を登録・更新します。order_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'order_id', required: true, desc: '注文ID（PK）' },
      { name: 'order_id_org', required: false, desc: '元注文ID' },
      { name: 'order_group_id', required: false, desc: '注文グループID' },
      { name: 'order_no', required: false, desc: '注文番号' },
      { name: 'bundle_child_order_ids', required: false, desc: '同梱子注文ID' },
      { name: 'bundle_parent_order_id', required: false, desc: '同梱親注文ID' },
      { name: 'bundle_order_bak', required: false, desc: '同梱注文バックアップ' },
      { name: 'user_id', required: false, desc: 'ユーザID' },
      { name: 'shop_id', required: false, desc: '店舗ID' },
      { name: 'supplier_id', required: false, desc: 'サプライヤID' },
      { name: 'order_kbn', required: false, desc: '注文区分' },
      { name: 'mall_id', required: false, desc: 'モールID' },
      { name: 'order_payment_kbn', required: false, desc: '支払区分' },
      { name: 'order_status', required: false, desc: '注文ステータス' },
      { name: 'order_date', required: false, desc: '注文日時' },
      { name: 'order_recognition_date', required: false, desc: '受注承認日時' },
      { name: 'order_stockreserved_status', required: false, desc: '在庫引当ステータス' },
      { name: 'order_stockreserved_date', required: false, desc: '在庫引当日時' },
      { name: 'order_shipping_date', required: false, desc: '出荷手配日時' },
      { name: 'order_shipped_status', required: false, desc: '出荷ステータス' },
      { name: 'order_shipped_date', required: false, desc: '出荷完了日時' },
      { name: 'order_delivering_date', required: false, desc: '配送完了日時' },
      { name: 'order_cancel_date', required: false, desc: 'キャンセル日時' },
      { name: 'order_return_date', required: false, desc: '返品日時' },
      { name: 'order_payment_status', required: false, desc: '入金ステータス' },
      { name: 'order_payment_date', required: false, desc: '入金確認日時' },
      { name: 'demand_status', required: false, desc: '督促ステータス' },
      { name: 'demand_date', required: false, desc: '督促日時' },
      { name: 'order_return_exchange_status', required: false, desc: '返品交換ステータス' },
      { name: 'order_return_exchange_receipt_date', required: false, desc: '返品交換受付日時' },
      { name: 'order_return_exchange_arrival_date', required: false, desc: '返品交換商品到着日時' },
      { name: 'order_return_exchange_complete_date', required: false, desc: '返品交換完了日時' },
      { name: 'order_repayment_status', required: false, desc: '返金ステータス' },
      { name: 'order_repayment_date', required: false, desc: '返金日時' },
      { name: 'order_item_count', required: false, desc: '注文アイテム数' },
      { name: 'order_product_count', required: false, desc: '注文商品数' },
      { name: 'order_price_subtotal', required: false, desc: '小計' },
      { name: 'order_price_pack', required: false, desc: '荷造金額' },
      { name: 'order_price_tax', required: false, desc: '税金合計' },
      { name: 'order_price_shipping', required: false, desc: '配送料' },
      { name: 'order_price_exchange', required: false, desc: '代引手数料' },
      { name: 'order_price_regulation', required: false, desc: '調整金額' },
      { name: 'order_price_repayment', required: false, desc: '返金金額' },
      { name: 'order_price_total', required: false, desc: '支払金額合計' },
      { name: 'order_discount_set_price', required: false, desc: 'セット値引金額' },
      { name: 'order_point_use', required: false, desc: '利用ポイント数' },
      { name: 'order_point_use_yen', required: false, desc: 'ポイント利用額' },
      { name: 'order_point_add', required: false, desc: '付与ポイント' },
      { name: 'order_point_rate', required: false, desc: 'ポイント調整率' },
      { name: 'order_coupon_use', required: false, desc: 'クーポン割引額' },
      { name: 'card_kbn', required: false, desc: '決済カード区分' },
      { name: 'card_instruments', required: false, desc: '決済カード支払回数文言' },
      { name: 'card_tran_id', required: false, desc: '決済カード取引ID' },
      { name: 'shipping_id', required: false, desc: '配送種別ID' },
      { name: 'fixed_purchase_id', required: false, desc: '定期購入ID' },
      { name: 'advcode_first', required: false, desc: '初回広告コード' },
      { name: 'advcode_new', required: false, desc: '最新広告コード' },
      { name: 'shipped_changed_kbn', required: false, desc: '出荷後変更区分' },
      { name: 'return_exchange_kbn', required: false, desc: '返品交換区分' },
      { name: 'return_exchange_reason_kbn', required: false, desc: '返品交換都合区分' },
      { name: 'attribute1', required: false, desc: '注文拡張項目1' },
      { name: 'attribute2', required: false, desc: '注文拡張項目2' },
      { name: 'attribute3', required: false, desc: '注文拡張項目3' },
      { name: 'attribute4', required: false, desc: '注文拡張項目4' },
      { name: 'attribute5', required: false, desc: '注文拡張項目5' },
      { name: 'attribute6', required: false, desc: '注文拡張項目6' },
      { name: 'attribute7', required: false, desc: '注文拡張項目7' },
      { name: 'attribute8', required: false, desc: '注文拡張項目8' },
      { name: 'attribute9', required: false, desc: '注文拡張項目9' },
      { name: 'attribute10', required: false, desc: '注文拡張項目10' },
      { name: 'extend_status1', required: false, desc: '拡張ステータス1' },
      { name: 'extend_status_date1', required: false, desc: '拡張ステータス日時1' },
      { name: 'extend_status2', required: false, desc: '拡張ステータス2' },
      { name: 'extend_status_date2', required: false, desc: '拡張ステータス日時2' },
      { name: 'extend_status3', required: false, desc: '拡張ステータス3' },
      { name: 'extend_status_date3', required: false, desc: '拡張ステータス日時3' },
      { name: 'extend_status4', required: false, desc: '拡張ステータス4' },
      { name: 'extend_status_date4', required: false, desc: '拡張ステータス日時4' },
      { name: 'extend_status5', required: false, desc: '拡張ステータス5' },
      { name: 'extend_status_date5', required: false, desc: '拡張ステータス日時5' },
      { name: 'extend_status6', required: false, desc: '拡張ステータス6' },
      { name: 'extend_status_date6', required: false, desc: '拡張ステータス日時6' },
      { name: 'extend_status7', required: false, desc: '拡張ステータス7' },
      { name: 'extend_status_date7', required: false, desc: '拡張ステータス日時7' },
      { name: 'extend_status8', required: false, desc: '拡張ステータス8' },
      { name: 'extend_status_date8', required: false, desc: '拡張ステータス日時8' },
      { name: 'extend_status9', required: false, desc: '拡張ステータス9' },
      { name: 'extend_status_date9', required: false, desc: '拡張ステータス日時9' },
      { name: 'extend_status10', required: false, desc: '拡張ステータス10' },
      { name: 'extend_status_date10', required: false, desc: '拡張ステータス日時10' },
      { name: 'remote_addr', required: false, desc: 'リモートIPアドレス' },
      { name: 'memo', required: false, desc: 'メモ' },
      { name: 'wrapping_memo', required: false, desc: 'ラッピングメモ' },
      { name: 'payment_memo', required: false, desc: '決済連携メモ' },
      { name: 'management_memo', required: false, desc: '管理メモ' },
      { name: 'relation_memo', required: false, desc: '外部連携メモ' },
      { name: 'return_exchange_reason_memo', required: false, desc: '返品交換理由メモ' },
      { name: 'regulation_memo', required: false, desc: '調整金額メモ' },
      { name: 'repayment_memo', required: false, desc: '返金メモ' },
      { name: 'del_flg', required: false, desc: '削除フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'member_rank_discount_price', required: false, desc: '会員ランク割引額' },
      { name: 'member_rank_id', required: false, desc: '注文時会員ランク' },
      { name: 'credit_branch_no', required: false, desc: 'クレジットカード枝番' },
      { name: 'affiliate_session_name1', required: false, desc: 'アフィリエイトセッション変数名1' },
      { name: 'affiliate_session_value1', required: false, desc: 'アフィリエイトセッション値1' },
      { name: 'affiliate_session_name2', required: false, desc: 'アフィリエイトセッション変数名2' },
      { name: 'affiliate_session_value2', required: false, desc: 'アフィリエイトセッション値2' },
      { name: 'user_agent', required: false, desc: 'ユーザーエージェント' },
      { name: 'gift_flg', required: false, desc: 'ギフト購入フラグ' },
      { name: 'digital_contents_flg', required: false, desc: 'デジタルコンテンツ購入フラグ' },
      { name: 'card_3dsecure_tran_id', required: false, desc: '3DセキュアトランザクションID' },
      { name: 'card_3dsecure_auth_url', required: false, desc: '3Dセキュア認証URL' },
      { name: 'card_3dsecure_auth_key', required: false, desc: '3Dセキュア認証キー' },
      { name: 'order_tax_included_flg', required: false, desc: '注文税込フラグ' },
      { name: 'order_tax_rate', required: false, desc: '注文税率(%)' },
      { name: 'order_tax_round_type', required: false, desc: '注文税計算方法' },
      { name: 'extend_status11', required: false, desc: '拡張ステータス11' },
      { name: 'extend_status_date11', required: false, desc: '拡張ステータス日時11' },
      { name: 'extend_status12', required: false, desc: '拡張ステータス12' },
      { name: 'extend_status_date12', required: false, desc: '拡張ステータス日時12' },
      { name: 'extend_status13', required: false, desc: '拡張ステータス13' },
      { name: 'extend_status_date13', required: false, desc: '拡張ステータス日時13' },
      { name: 'extend_status14', required: false, desc: '拡張ステータス14' },
      { name: 'extend_status_date14', required: false, desc: '拡張ステータス日時14' },
      { name: 'extend_status15', required: false, desc: '拡張ステータス15' },
      { name: 'extend_status_date15', required: false, desc: '拡張ステータス日時15' },
      { name: 'extend_status16', required: false, desc: '拡張ステータス16' },
      { name: 'extend_status_date16', required: false, desc: '拡張ステータス日時16' },
      { name: 'extend_status17', required: false, desc: '拡張ステータス17' },
      { name: 'extend_status_date17', required: false, desc: '拡張ステータス日時17' },
      { name: 'extend_status18', required: false, desc: '拡張ステータス18' },
      { name: 'extend_status_date18', required: false, desc: '拡張ステータス日時18' },
      { name: 'extend_status19', required: false, desc: '拡張ステータス19' },
      { name: 'extend_status_date19', required: false, desc: '拡張ステータス日時19' },
      { name: 'extend_status20', required: false, desc: '拡張ステータス20' },
      { name: 'extend_status_date20', required: false, desc: '拡張ステータス日時20' },
      { name: 'extend_status21', required: false, desc: '拡張ステータス21' },
      { name: 'extend_status_date21', required: false, desc: '拡張ステータス日時21' },
      { name: 'extend_status22', required: false, desc: '拡張ステータス22' },
      { name: 'extend_status_date22', required: false, desc: '拡張ステータス日時22' },
      { name: 'extend_status23', required: false, desc: '拡張ステータス23' },
      { name: 'extend_status_date23', required: false, desc: '拡張ステータス日時23' },
      { name: 'extend_status24', required: false, desc: '拡張ステータス24' },
      { name: 'extend_status_date24', required: false, desc: '拡張ステータス日時24' },
      { name: 'extend_status25', required: false, desc: '拡張ステータス25' },
      { name: 'extend_status_date25', required: false, desc: '拡張ステータス日時25' },
      { name: 'extend_status26', required: false, desc: '拡張ステータス26' },
      { name: 'extend_status_date26', required: false, desc: '拡張ステータス日時26' },
      { name: 'extend_status27', required: false, desc: '拡張ステータス27' },
      { name: 'extend_status_date27', required: false, desc: '拡張ステータス日時27' },
      { name: 'extend_status28', required: false, desc: '拡張ステータス28' },
      { name: 'extend_status_date28', required: false, desc: '拡張ステータス日時28' },
      { name: 'extend_status29', required: false, desc: '拡張ステータス29' },
      { name: 'extend_status_date29', required: false, desc: '拡張ステータス日時29' },
      { name: 'extend_status30', required: false, desc: '拡張ステータス30' },
      { name: 'extend_status_date30', required: false, desc: '拡張ステータス日時30' },
      { name: 'card_installments_code', required: false, desc: 'カード支払い回数コード' },
      {
        name: 'setpromotion_product_discount_amount',
        required: false,
        desc: 'セットプロモーション商品割引額'
      },
      {
        name: 'setpromotion_shipping_charge_discount_amount',
        required: false,
        desc: 'セットプロモーション配送料割引額'
      },
      {
        name: 'setpromotion_payment_charge_discount_amount',
        required: false,
        desc: 'セットプロモーション決済手数料割引額'
      },
      { name: 'online_payment_status', required: false, desc: 'オンライン決済ステータス' },
      { name: 'fixed_purchase_order_count', required: false, desc: '定期購入回数(注文時点)' },
      { name: 'fixed_purchase_shipped_count', required: false, desc: '定期購入回数(出荷時点)' },
      { name: 'fixed_purchase_discount_price', required: false, desc: '定期購入割引額' },
      { name: 'payment_order_id', required: false, desc: '決済注文ID' },
      { name: 'fixed_purchase_member_discount_amount', required: false, desc: '定期会員割引額' },
      { name: 'last_billed_amount', required: false, desc: '最終請求金額' },
      { name: 'external_payment_status', required: false, desc: '外部決済ステータス' },
      { name: 'external_payment_error_message', required: false, desc: '外部決済エラーメッセージ' },
      { name: 'external_payment_auth_date', required: false, desc: '外部決済与信日時' },
      { name: 'extend_status31', required: false, desc: '拡張ステータス31' },
      { name: 'extend_status32', required: false, desc: '拡張ステータス32' },
      { name: 'extend_status33', required: false, desc: '拡張ステータス33' },
      { name: 'extend_status34', required: false, desc: '拡張ステータス34' },
      { name: 'extend_status35', required: false, desc: '拡張ステータス35' },
      { name: 'extend_status36', required: false, desc: '拡張ステータス36' },
      { name: 'extend_status37', required: false, desc: '拡張ステータス37' },
      { name: 'extend_status38', required: false, desc: '拡張ステータス38' },
      { name: 'extend_status39', required: false, desc: '拡張ステータス39' },
      { name: 'extend_status40', required: false, desc: '拡張ステータス40' },
      { name: 'extend_status_date31', required: false, desc: '拡張ステータス日時31' },
      { name: 'extend_status_date32', required: false, desc: '拡張ステータス日時32' },
      { name: 'extend_status_date33', required: false, desc: '拡張ステータス日時33' },
      { name: 'extend_status_date34', required: false, desc: '拡張ステータス日時34' },
      { name: 'extend_status_date35', required: false, desc: '拡張ステータス日時35' },
      { name: 'extend_status_date36', required: false, desc: '拡張ステータス日時36' },
      { name: 'extend_status_date37', required: false, desc: '拡張ステータス日時37' },
      { name: 'extend_status_date38', required: false, desc: '拡張ステータス日時38' },
      { name: 'extend_status_date39', required: false, desc: '拡張ステータス日時39' },
      { name: 'extend_status_date40', required: false, desc: '拡張ステータス日時40' },
      { name: 'last_order_point_use', required: false, desc: '最終利用ポイント数' },
      { name: 'last_order_point_use_yen', required: false, desc: '最終ポイント利用額' },
      { name: 'external_order_id', required: false, desc: '外部連携受注ID' },
      { name: 'external_import_status', required: false, desc: '外部連携取込ステータス' },
      { name: 'combined_org_order_ids', required: false, desc: '注文同梱元注文ID' },
      { name: 'last_auth_flg', required: false, desc: '最終与信フラグ' },
      { name: 'mall_link_status', required: false, desc: 'モール連携ステータス' },
      { name: 'order_price_subtotal_tax', required: false, desc: '小計税額' },
      { name: 'order_price_shipping_tax', required: false, desc: '配送料税額' },
      { name: 'order_price_exchange_tax', required: false, desc: '代引手数料税額' },
      { name: 'settlement_currency', required: false, desc: '決済通貨' },
      { name: 'settlement_rate', required: false, desc: '決済レート' },
      { name: 'settlement_amount', required: false, desc: '決済金額' },
      { name: 'shipping_memo', required: false, desc: '配送メモ' },
      { name: 'external_payment_cooperation_log', required: false, desc: '外部連携決済ログ' },
      { name: 'shipping_tax_rate', required: false, desc: '配送料税率' },
      { name: 'payment_tax_rate', required: false, desc: '決済手数料税率' },
      { name: 'order_count_order', required: false, desc: '購入回数（注文基準）' },
      { name: 'invoice_bundle_flg', required: false, desc: '請求書同梱フラグ' },
      { name: 'inflow_contents_id', required: false, desc: '流入コンテンツID' },
      { name: 'inflow_contents_type', required: false, desc: '流入コンテンツタイプ' },
      { name: 'receipt_flg', required: false, desc: '領収書希望フラグ' },
      { name: 'receipt_output_flg', required: false, desc: '領収書出力フラグ' },
      { name: 'receipt_address', required: false, desc: '宛名' },
      { name: 'receipt_proviso', required: false, desc: '但し書き' },
      { name: 'delivery_tran_id', required: false, desc: '物流取引ID' },
      { name: 'online_delivery_status', required: false, desc: 'オンライン物流ステータス' },
      { name: 'external_payment_type', required: false, desc: '外部決済タイプ' },
      { name: 'Logi_cooperation_status', required: false, desc: '物流連携ステータス' },
      { name: 'extend_status41', required: false, desc: '拡張ステータス41' },
      { name: 'extend_status42', required: false, desc: '拡張ステータス42' },
      { name: 'extend_status43', required: false, desc: '拡張ステータス43' },
      { name: 'extend_status44', required: false, desc: '拡張ステータス44' },
      { name: 'extend_status45', required: false, desc: '拡張ステータス45' },
      { name: 'extend_status46', required: false, desc: '拡張ステータス46' },
      { name: 'extend_status47', required: false, desc: '拡張ステータス47' },
      { name: 'extend_status48', required: false, desc: '拡張ステータス48' },
      { name: 'extend_status49', required: false, desc: '拡張ステータス49' },
      { name: 'extend_status50', required: false, desc: '拡張ステータス50' },
      { name: 'extend_status_date41', required: false, desc: '拡張ステータス日時41' },
      { name: 'extend_status_date42', required: false, desc: '拡張ステータス日時42' },
      { name: 'extend_status_date43', required: false, desc: '拡張ステータス日時43' },
      { name: 'extend_status_date44', required: false, desc: '拡張ステータス日時44' },
      { name: 'extend_status_date45', required: false, desc: '拡張ステータス日時45' },
      { name: 'extend_status_date46', required: false, desc: '拡張ステータス日時46' },
      { name: 'extend_status_date47', required: false, desc: '拡張ステータス日時47' },
      { name: 'extend_status_date48', required: false, desc: '拡張ステータス日時48' },
      { name: 'extend_status_date49', required: false, desc: '拡張ステータス日時49' },
      { name: 'extend_status_date50', required: false, desc: '拡張ステータス日時50' },
      { name: 'card_tran_pass', required: false, desc: '決済カード取引PASS' },
      {
        name: 'member_rank_discount_price_shipping',
        required: false,
        desc: '会員ランク送料割引額'
      },
      { name: 'order_coupon_use_shipping', required: false, desc: 'クーポン送料割引額' },
      { name: 'medical_questionnaire_data', required: false, desc: '問診票入力データ' },
      { name: 'real_shop_id', required: false, desc: 'リアル店舗ID' },
      { name: 'staff_id', required: false, desc: 'スタッフID' }
    ],
    sample:
      'order_id,order_no,user_id,shop_id,order_kbn,order_status,order_date,order_price_total,order_payment_status,del_flg\nORD001,N0001,USR0000001,SHOP1,0,COMPLETED,2026-03-01 10:30:00,8960,PAID,0'
  },
  {
    type: 'w2_OrderItem',
    filePrefix: 'w2_order_item_',
    description: 'W2受注明細（全72項目）を登録・更新します。order_id+order_item_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'order_id', required: true, desc: '注文ID（PK1）' },
      { name: 'order_item_no', required: true, desc: '注文商品枝番（PK2）' },
      { name: 'order_shipping_no', required: false, desc: '配送先枝番' },
      { name: 'shop_id', required: false, desc: '店舗ID' },
      { name: 'product_id', required: false, desc: '商品ID' },
      { name: 'variation_id', required: false, desc: '商品バリエーションID' },
      { name: 'supplier_id', required: false, desc: 'サプライヤID' },
      { name: 'product_name', required: false, desc: '商品名' },
      { name: 'product_name_kana', required: false, desc: '商品名かな' },
      { name: 'product_price', required: false, desc: '商品単価' },
      { name: 'product_price_org', required: false, desc: '商品単価（値引き前）' },
      { name: 'product_point', required: false, desc: '商品ポイント' },
      { name: 'product_tax_included_flg', required: false, desc: '税込フラグ' },
      { name: 'product_tax_rate', required: false, desc: '税率(%)' },
      { name: 'product_tax_round_type', required: false, desc: '税計算方法' },
      { name: 'product_price_pretax', required: false, desc: '税込販売価格' },
      { name: 'product_price_ship', required: false, desc: '配送料' },
      { name: 'product_price_cost', required: false, desc: '商品原価' },
      { name: 'product_point_kbn', required: false, desc: '付与ポイント区分' },
      { name: 'item_realstock_reserved', required: false, desc: '実在庫引当済み商品数' },
      { name: 'item_realstock_shipped', required: false, desc: '実在庫出荷済み商品数' },
      { name: 'item_quantity', required: false, desc: '注文数' },
      { name: 'item_quantity_single', required: false, desc: '注文数（セット未考慮）' },
      { name: 'item_price', required: false, desc: '明細金額（小計）' },
      { name: 'item_price_single', required: false, desc: '明細金額（セット未考慮）' },
      { name: 'product_set_id', required: false, desc: '商品セットID' },
      { name: 'product_set_no', required: false, desc: '商品セット枝番' },
      { name: 'product_set_count', required: false, desc: '商品セット注文数' },
      { name: 'item_return_exchange_kbn', required: false, desc: '返品交換区分' },
      { name: 'item_memo', required: false, desc: '明細備考' },
      { name: 'item_point', required: false, desc: '明細ポイント' },
      { name: 'item_cancel_flg', required: false, desc: 'キャンセルフラグ' },
      { name: 'item_cancel_date', required: false, desc: 'キャンセル日時' },
      { name: 'item_return_flg', required: false, desc: '返品フラグ' },
      { name: 'item_return_date', required: false, desc: '返品日時' },
      { name: 'del_flg', required: false, desc: '削除フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'product_option_texts', required: false, desc: '商品付帯情報選択値' },
      { name: 'brand_id', required: false, desc: 'ブランドID' },
      { name: 'download_url', required: false, desc: 'ダウンロードURL' },
      { name: 'productsale_id', required: false, desc: '商品セールID' },
      { name: 'cooperation_id1', required: false, desc: '商品連携ID1' },
      { name: 'cooperation_id2', required: false, desc: '商品連携ID2' },
      { name: 'cooperation_id3', required: false, desc: '商品連携ID3' },
      { name: 'cooperation_id4', required: false, desc: '商品連携ID4' },
      { name: 'cooperation_id5', required: false, desc: '商品連携ID5' },
      { name: 'stock_returned_flg', required: false, desc: '在庫戻し済みフラグ' },
      { name: 'novelty_id', required: false, desc: 'ノベルティID' },
      { name: 'recommend_id', required: false, desc: 'レコメンドID' },
      { name: 'fixed_purchase_product_flg', required: false, desc: '定期購入フラグ' },
      { name: 'product_bundle_id', required: false, desc: '商品同梱ID' },
      { name: 'bundle_item_display_type', required: false, desc: '同梱商品明細表示フラグ' },
      { name: 'order_history_display_type', required: false, desc: '明細表示フラグ（計算列）' },
      { name: 'limited_payment_ids', required: false, desc: '決済利用不可' },
      { name: 'plural_shipping_price_free_flg', required: false, desc: '配送料金複数個無料フラグ' },
      { name: 'shipping_size_kbn', required: false, desc: '配送サイズ区分' },
      { name: 'column_for_mall_order', required: false, desc: 'モール用項目' },
      { name: 'cooperation_id6', required: false, desc: '商品連携ID6' },
      { name: 'cooperation_id7', required: false, desc: '商品連携ID7' },
      { name: 'cooperation_id8', required: false, desc: '商品連携ID8' },
      { name: 'cooperation_id9', required: false, desc: '商品連携ID9' },
      { name: 'cooperation_id10', required: false, desc: '商品連携ID10' },
      { name: 'gift_wrapping_id', required: false, desc: 'ギフト包装ID' },
      { name: 'gift_message', required: false, desc: 'ギフトメッセージ' },
      { name: 'item_price_tax', required: false, desc: '明細金額（税金額）' },
      { name: 'fixed_purchase_discount_value', required: false, desc: '定期購入値引' },
      { name: 'fixed_purchase_discount_type', required: false, desc: '定期購入値引タイプ' },
      {
        name: 'fixed_purchase_item_order_count',
        required: false,
        desc: '定期商品購入回数(注文時点)'
      },
      {
        name: 'fixed_purchase_item_shipped_count',
        required: false,
        desc: '定期商品購入回数(出荷時点)'
      },
      { name: 'item_discounted_price', required: false, desc: '明細金額（割引後価格）' },
      { name: 'temperature_zones', required: false, desc: '配送可能温度帯' }
    ],
    sample:
      'order_id,order_item_no,shop_id,product_id,variation_id,product_name,product_price,item_quantity,item_price\nORD001,1,SHOP1,PRD001,VAR001,Tシャツ 白,2980,2,5960\nORD001,2,SHOP1,PRD002,VAR001,デニムパンツ,5980,1,5980'
  },
  {
    type: 'w2_OrderCoupon',
    filePrefix: 'w2_order_coupon_',
    description: 'W2受注クーポン（全14項目）を登録・更新します。order_id+order_coupon_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'order_id', required: true, desc: '注文ID（PK1）' },
      { name: 'order_coupon_no', required: true, desc: '注文クーポン枝番（PK2）' },
      { name: 'dept_id', required: false, desc: '識別ID' },
      { name: 'coupon_id', required: false, desc: 'クーポンID' },
      { name: 'coupon_no', required: false, desc: '枝番' },
      { name: 'coupon_code', required: false, desc: 'クーポンコード' },
      { name: 'coupon_name', required: false, desc: '管理用クーポン名' },
      { name: 'coupon_disp_name', required: false, desc: '表示用クーポン名' },
      { name: 'coupon_type', required: false, desc: 'クーポン種別' },
      { name: 'coupon_discount_price', required: false, desc: 'クーポン割引額' },
      { name: 'coupon_discount_rate', required: false, desc: 'クーポン割引率' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'order_id,order_coupon_no,coupon_id,coupon_code,coupon_name,coupon_discount_price\nORD001,1,CPN001,SPRING,春割クーポン,500'
  },
  {
    type: 'w2_Coupon',
    filePrefix: 'w2_coupon_',
    description: 'W2クーポンマスタ（全34項目）を登録・更新します。dept_id+coupon_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'dept_id', required: true, desc: '識別ID（PK1）' },
      { name: 'coupon_id', required: true, desc: 'クーポンID（PK2）' },
      { name: 'coupon_code', required: false, desc: 'クーポンコード' },
      { name: 'coupon_name', required: false, desc: '管理用クーポン名' },
      { name: 'coupon_disp_name', required: false, desc: '表示用クーポン名' },
      { name: 'coupon_discription', required: false, desc: 'クーポン説明文' },
      { name: 'coupon_type', required: false, desc: 'クーポン種別' },
      { name: 'coupon_count', required: false, desc: 'クーポン利用可能回数' },
      { name: 'publish_date_bgn', required: false, desc: 'クーポン発行期間(開始)' },
      { name: 'publish_date_end', required: false, desc: 'クーポン発行期間(終了)' },
      { name: 'discount_price', required: false, desc: 'クーポン商品割引額' },
      { name: 'discount_rate', required: false, desc: 'クーポン商品割引率' },
      { name: 'expire_day', required: false, desc: 'クーポン有効期限(日)' },
      { name: 'expire_date_bgn', required: false, desc: 'クーポン有効期間(開始)' },
      { name: 'expire_date_end', required: false, desc: 'クーポン有効期間(終了)' },
      { name: 'product_kbn', required: false, desc: 'クーポン対象商品区分' },
      { name: 'exceptional_product', required: false, desc: '例外商品' },
      { name: 'exceptional_icon', required: false, desc: '例外商品アイコン' },
      { name: 'usable_price', required: false, desc: 'クーポン利用最低購入金額' },
      { name: 'use_together_flg', required: false, desc: 'クーポン併用フラグ' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'disp_flg', required: false, desc: 'フロント表示フラグ' },
      { name: 'coupon_disp_discription', required: false, desc: 'フロント表示用クーポン説明文' },
      { name: 'exceptional_brand_ids', required: false, desc: '例外ブランドID' },
      { name: 'exceptional_product_category_ids', required: false, desc: '例外商品カテゴリID' },
      { name: 'free_shipping_flg', required: false, desc: 'クーポン配送料無料フラグ' },
      { name: 'applicable_product', required: false, desc: '対象商品' },
      { name: 'applicable_brand_ids', required: false, desc: '対象ブランドID' },
      { name: 'applicable_product_category_ids', required: false, desc: '対象商品カテゴリID' },
      { name: 'applicable_icon', required: false, desc: '対象商品アイコン' },
      { name: 'applicable_condition_operator', required: false, desc: '対象条件結合方法' }
    ],
    sample:
      'dept_id,coupon_id,coupon_code,coupon_name,coupon_type,discount_price,discount_rate,valid_flg\nDEPT01,CPN001,SPRING,春割クーポン,FIXED,500,,ON'
  },
  {
    type: 'w2_AdvCode',
    filePrefix: 'w2_adv_code_',
    description: 'W2広告コードマスタ（全16項目）を登録・更新します。advcode_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'advcode_no', required: true, desc: '広告コードNo（PK）' },
      { name: 'dept_id', required: false, desc: '識別ID' },
      { name: 'advertisement_code', required: false, desc: '広告コード' },
      { name: 'media_name', required: false, desc: '媒体名' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'advertisement_date', required: false, desc: '出稿日' },
      { name: 'media_cost', required: false, desc: '媒体費' },
      { name: 'memo', required: false, desc: '備考' },
      { name: 'publication_date_from', required: false, desc: '掲載期間(From)' },
      { name: 'publication_date_to', required: false, desc: '掲載期間(To)' },
      { name: 'advcode_media_type_id', required: false, desc: '広告媒体区分' },
      {
        name: 'member_rank_id_granted_at_account_registration',
        required: false,
        desc: '会員登録時紐づけ会員ランクID'
      },
      {
        name: 'user_management_level_id_granted_at_account_registration',
        required: false,
        desc: '会員登録時紐づけユーザー管理レベルID'
      }
    ],
    sample:
      'advcode_no,dept_id,advertisement_code,media_name,advcode_media_type_id,valid_flg\n1,DEPT01,ADV001,Google,SEARCH,ON'
  },
  {
    type: 'w2_FeaturePage',
    filePrefix: 'w2_feature_page_',
    description: 'W2特集ページ（全26項目）を登録・更新します。feature_page_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'feature_page_id', required: true, desc: '特集ページID（PK）' },
      { name: 'management_title', required: false, desc: '管理用タイトル' },
      { name: 'page_type', required: false, desc: 'ページタイプ' },
      { name: 'category_id', required: false, desc: '特集ページカテゴリID' },
      { name: 'permitted_brand_ids', required: false, desc: '許可ブランドID' },
      { name: 'file_name', required: false, desc: 'ファイル名' },
      { name: 'file_dir_path', required: false, desc: 'ディレクトリパス' },
      { name: 'page_sort_number', required: false, desc: 'ページ順序' },
      { name: 'use_type', required: false, desc: 'ページの利用状態' },
      { name: 'publish', required: false, desc: '公開状態' },
      { name: 'condition_publish_date_from', required: false, desc: '公開開始日' },
      { name: 'condition_publish_date_to', required: false, desc: '公開終了日' },
      { name: 'condition_member_only_type', required: false, desc: '会員限定コンテンツ' },
      { name: 'condition_member_rank_id', required: false, desc: '一部会員公開用会員ランク' },
      { name: 'condition_target_list_type', required: false, desc: 'ターゲットリスト検索条件' },
      { name: 'condition_target_list_ids', required: false, desc: '対象ターゲットリスト' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'metadata_desc', required: false, desc: 'SEOディスクリプション' },
      { name: 'html_page_title', required: false, desc: 'HTMLページタイトル' },
      { name: 'sitemap_include', required: false, desc: 'サイトマップ出力要否' },
      { name: 'sitemap_changefreq', required: false, desc: 'サイトマップ更新頻度' },
      { name: 'sitemap_priority', required: false, desc: 'サイトマップ優先度' },
      { name: 'contents_head', required: false, desc: '記事HEAD（class範囲内metaの連結）' },
      { name: 'keyword', required: false, desc: 'キーワード' }
    ],
    sample:
      'feature_page_id,management_title,page_type,category_id,publish,page_sort_number,use_type\nFEAT001,春のキャンペーン,NORMAL,FCAT001,PUBLIC,1,USE'
  },
  {
    type: 'w2_FeaturePageCategory',
    filePrefix: 'w2_feature_page_category_',
    description:
      'W2特集ページカテゴリ（全11項目）を登録・更新します。shop_id+category_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID（PK1）' },
      { name: 'category_id', required: true, desc: 'カテゴリID（PK2）' },
      { name: 'parent_category_id', required: false, desc: '親カテゴリID' },
      { name: 'category_name', required: false, desc: 'カテゴリ名' },
      { name: 'category_outline', required: false, desc: 'カテゴリ概要' },
      { name: 'display_order', required: false, desc: '表示順' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'slug', required: false, desc: 'スラッグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'shop_id,category_id,parent_category_id,category_name,display_order,valid_flg\nSHOP1,FCAT001,,春物アパレル,1,ON'
  },
  {
    type: 'w2_FeaturePageContents',
    filePrefix: 'w2_feature_page_contents_',
    description:
      'W2特集ページコンテンツ（全15項目）を登録・更新します。feature_page_id+contents_kbn+contents_type+contents_sort_numberでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'feature_page_id', required: true, desc: '特集ページID（PK1）' },
      { name: 'contents_kbn', required: true, desc: 'コンテンツ区分（PK2）' },
      { name: 'contents_type', required: true, desc: 'コンテンツタイプ（PK3）' },
      { name: 'contents_sort_number', required: true, desc: 'コンテンツ表示順序（PK4）' },
      { name: 'page_title', required: false, desc: 'ページタイトル（PAGE_TITLE用）' },
      { name: 'alt_text', required: false, desc: '代替テキスト（HEADER_BANNER用）' },
      { name: 'product_group_id', required: false, desc: '商品グループID（PRODUCT_LIST用）' },
      { name: 'product_list_title', required: false, desc: '商品一覧タイトル（PRODUCT_LIST用）' },
      { name: 'display_number', required: false, desc: '表示件数（PRODUCT_LIST用）' },
      { name: 'pagination_flg', required: false, desc: 'ページ送りフラグ' },
      { name: 'image_path', required: false, desc: '画像パス' },
      { name: 'contents_html', required: false, desc: '記事本文HTML' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'feature_page_id,contents_kbn,contents_type,contents_sort_number,page_title,product_group_id,display_number\nFEAT001,PRODUCT_LIST,PRODUCT,1,おすすめ,GRP001,10'
  },
  {
    type: 'w2_ContentsTag',
    filePrefix: 'w2_contents_tag_',
    description: 'W2コンテンツタグ（全4項目）を登録・更新します。contents_tag_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'contents_tag_id', required: true, desc: 'コンテンツタグID（PK）' },
      { name: 'contents_tag_name', required: false, desc: 'コンテンツタグ名' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' }
    ],
    sample:
      'contents_tag_id,contents_tag_name,date_created,date_changed\nCTAG001,夏物,2026-03-01,2026-03-01'
  },
  {
    type: 'w2_ProductGroup',
    filePrefix: 'w2_product_group_',
    description: 'W2商品グループ（全10項目）を登録・更新します。product_group_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'product_group_id', required: true, desc: '商品グループID（PK）' },
      { name: 'product_group_name', required: false, desc: '商品グループ名' },
      { name: 'begin_date', required: false, desc: '開始日時' },
      { name: 'end_date', required: false, desc: '終了日時（NULL:未設定＝有効期間有効）' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'product_group_page_contents_kbn', required: false, desc: '表示内容HTML区分' },
      { name: 'product_group_page_contents', required: false, desc: '表示内容' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'product_group_id,product_group_name,begin_date,end_date,valid_flg\nGRP001,おすすめ商品,2026-03-01,,ON'
  },
  {
    type: 'w2_ProductGroupItem',
    filePrefix: 'w2_product_group_item_',
    description:
      'W2商品グループ明細（全7項目）を登録・更新します。product_group_id+item_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'product_group_id', required: true, desc: '商品特集グループID（PK1）' },
      { name: 'item_no', required: true, desc: '商品グループアイテム枝番（PK2）' },
      { name: 'item_type', required: false, desc: 'アイテムタイプ' },
      { name: 'shop_id', required: false, desc: '店舗ID' },
      { name: 'master_id', required: false, desc: 'マスターID（商品ID指定時：商品ID）' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'product_group_id,item_no,item_type,shop_id,master_id\nGRP001,1,PRODUCT,SHOP1,PRD001\nGRP001,2,PRODUCT,SHOP1,PRD002'
  },
  // 施策系マスタ（add-w2-marketing-master-tables）。列説明は data-model.md 由来
  {
    type: 'w2_UserPoint',
    filePrefix: 'w2_user_point_',
    description: 'ユーザポイント（全20項目）を登録・更新します。user_id+point_kbn_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'user_id', required: true, desc: 'ユーザID' },
      { name: 'point_kbn', required: false, desc: 'ポイント区分' },
      { name: 'point_kbn_no', required: true, desc: '枝番' },
      { name: 'dept_id', required: false, desc: '識別ID' },
      { name: 'point_rule_id', required: false, desc: 'ポイントルールID' },
      { name: 'point_rule_kbn', required: false, desc: 'ポイントルール区分' },
      { name: 'point_type', required: false, desc: 'ポイント種別' },
      { name: 'point_inc_kbn', required: false, desc: 'ポイント加算区分' },
      { name: 'point', required: false, desc: 'ポイント数' },
      { name: 'point_exp', required: false, desc: '有効期限' },
      { name: 'history_no', required: false, desc: '枝番（ユーザポイント履歴用）' },
      { name: 'kbn1', required: false, desc: '予備区分1' },
      { name: 'kbn2', required: false, desc: '予備区分2' },
      { name: 'kbn3', required: false, desc: '予備区分3' },
      { name: 'kbn4', required: false, desc: '予備区分4' },
      { name: 'kbn5', required: false, desc: '予備区分5' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'effective_date', required: false, desc: '発効日' }
    ],
    sample:
      'user_id,point_kbn,point_kbn_no,dept_id,point_rule_id,point_rule_kbn,point_type,point_inc_kbn\n1,sample,1,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_UserPointHistory',
    filePrefix: 'w2_user_point_history_',
    description:
      'ユーザポイント履歴（全25項目）を登録・更新します。tgt_year+tgt_month+tgt_day+user_id+history_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'user_id', required: true, desc: 'ユーザID' },
      { name: 'history_no', required: true, desc: '枝番' },
      { name: 'tgt_year', required: true, desc: '対象年' },
      { name: 'tgt_month', required: true, desc: '対象月' },
      { name: 'tgt_day', required: true, desc: '対象日' },
      { name: 'dept_id', required: false, desc: '識別ID' },
      { name: 'point_rule_id', required: false, desc: 'ポイントルールID' },
      { name: 'point_rule_kbn', required: false, desc: 'ポイントルール区分' },
      { name: 'point_kbn', required: false, desc: 'ポイント区分' },
      { name: 'point_type', required: false, desc: 'ポイント種別' },
      { name: 'point_inc_kbn', required: false, desc: 'ポイント加算区分' },
      { name: 'point_inc', required: false, desc: 'ポイント加算数' },
      { name: 'point_exp_extend', required: false, desc: 'ポイント有効期限延長' },
      { name: 'user_point_exp', required: false, desc: 'ユーザ最新有効期限' },
      { name: 'kbn1', required: false, desc: '予備区分1' },
      { name: 'kbn2', required: false, desc: '予備区分2' },
      { name: 'kbn3', required: false, desc: '予備区分3' },
      { name: 'kbn4', required: false, desc: '予備区分4' },
      { name: 'kbn5', required: false, desc: '予備区分5' },
      { name: 'memo', required: false, desc: 'メモ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'effective_date', required: false, desc: '発効日' },
      { name: 'restored_flg', required: false, desc: '戻し処理済フラグ' },
      { name: 'history_group_no', required: false, desc: '履歴グループ番号' }
    ],
    sample:
      'user_id,history_no,tgt_year,tgt_month,tgt_day,dept_id,point_rule_id,point_rule_kbn\n1,1,1,1,1,sample,sample,sample'
  },
  {
    type: 'w2_PointRule',
    filePrefix: 'w2_point_rule_',
    description: 'ポイントルール（全36項目）を登録・更新します。dept_id+point_rule_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'dept_id', required: true, desc: '識別ID' },
      { name: 'point_rule_id', required: true, desc: 'ポイントルールID' },
      { name: 'point_rule_name', required: false, desc: 'ポイントルール名' },
      { name: 'point_rule_kbn', required: false, desc: 'ポイントルール区分' },
      { name: 'point_kbn', required: false, desc: '対象ポイント区分' },
      { name: 'use_temp_flg', required: false, desc: '仮ポイント使用フラグ' },
      { name: 'point_inc_kbn', required: false, desc: 'ポイント加算区分' },
      { name: 'inc_type', required: false, desc: 'ポイント加算方法' },
      { name: 'inc_num', required: false, desc: 'ポイント加算数' },
      { name: 'inc_rate', required: false, desc: 'ポイント加算率' },
      { name: 'point_exp_extend', required: false, desc: 'ポイント有効期限延長' },
      { name: 'exp_bgn', required: false, desc: '有効期間（開始）' },
      { name: 'exp_end', required: false, desc: '有効期間（終了）' },
      { name: 'campaign_term_kbn', required: false, desc: 'キャンペーン期間区分' },
      { name: 'campaign_term_value', required: false, desc: 'キャンペーン期間値' },
      { name: 'priority', required: false, desc: '優先順位' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'kbn1', required: false, desc: '予備区分1' },
      { name: 'kbn2', required: false, desc: '予備区分2' },
      { name: 'kbn3', required: false, desc: '予備区分3' },
      { name: 'kbn4', required: false, desc: '予備区分4' },
      { name: 'kbn5', required: false, desc: '予備区分5' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'point_rule_disp_name', required: false, desc: '表示用ポイントルール名' },
      { name: 'fixed_purchase_inc_type', required: false, desc: '定期購入ポイント加算方法' },
      { name: 'fixed_purchase_inc_num', required: false, desc: '定期購入ポイント加算数' },
      { name: 'fixed_purchase_inc_rate', required: false, desc: '定期購入ポイント加算率' },
      { name: 'effective_offset', required: false, desc: '期間限定ポイント発効オフセット' },
      {
        name: 'effective_offset_type',
        required: false,
        desc: '期間限定ポイント発効オフセット種別'
      },
      { name: 'term', required: false, desc: '期間限定ポイント有効期間（相対）' },
      { name: 'term_type', required: false, desc: '期間限定ポイント有効期間（相対）種別' },
      { name: 'period_begin', required: false, desc: '期間限定ポイント有効期間（絶対）開始' },
      { name: 'period_end', required: false, desc: '期間限定ポイント有効期間（絶対）終了' },
      { name: 'allow_duplicate_apply_flg', required: false, desc: '基本ルールとの二重適用' }
    ],
    sample:
      'dept_id,point_rule_id,point_rule_name,point_rule_kbn,point_kbn,use_temp_flg,point_inc_kbn,inc_type\n1,1,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_PointRuleSchedule',
    filePrefix: 'w2_point_rule_schedule_',
    description:
      'ポイントルールスケジュールテーブル（全22項目）を登録・更新します。point_rule_schedule_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'point_rule_schedule_id', required: true, desc: 'ポイントルールスケジュールID' },
      { name: 'point_rule_schedule_name', required: false, desc: 'ポイントルールスケジュール名' },
      { name: 'status', required: false, desc: 'ステータス' },
      { name: 'last_count', required: false, desc: '最終付与人数' },
      { name: 'last_exec_date', required: false, desc: '最終付与日時' },
      { name: 'target_id', required: false, desc: 'ターゲットID' },
      { name: 'target_extract_flg', required: false, desc: 'ターゲット抽出フラグ' },
      { name: 'point_rule_id', required: false, desc: 'ポイントルールID' },
      { name: 'mail_id', required: false, desc: 'メールテンプレートID' },
      { name: 'exec_timing', required: false, desc: '実行タイミング' },
      { name: 'schedule_kbn', required: false, desc: 'スケジュール区分' },
      { name: 'schedule_day_of_week', required: false, desc: 'スケジュール曜日' },
      { name: 'schedule_year', required: false, desc: 'スケジュール日程(年)' },
      { name: 'schedule_month', required: false, desc: 'スケジュール日程(月)' },
      { name: 'schedule_day', required: false, desc: 'スケジュール日程(日)' },
      { name: 'schedule_hour', required: false, desc: 'スケジュール日程(時)' },
      { name: 'schedule_minute', required: false, desc: 'スケジュール日程(分)' },
      { name: 'schedule_second', required: false, desc: 'スケジュール日程(秒)' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'point_rule_schedule_id,point_rule_schedule_name,status,last_count,last_exec_date,target_id,target_extract_flg,point_rule_id\n1,sample,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_UserMemberRankHistory',
    filePrefix: 'w2_user_member_rank_history_',
    description: '会員ランク更新履歴（全7項目）を登録・更新します。history_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'history_no', required: true, desc: '履歴No' },
      { name: 'user_id', required: false, desc: 'ユーザID' },
      { name: 'before_rank_id', required: false, desc: '更新前ランクID' },
      { name: 'after_rank_id', required: false, desc: '更新後ランクID' },
      { name: 'mail_id', required: false, desc: 'メールテンプレートID' },
      { name: 'changed_by', required: false, desc: '変更者' },
      { name: 'date_created', required: false, desc: '作成日' }
    ],
    sample:
      'history_no,user_id,before_rank_id,after_rank_id,mail_id,changed_by,date_created\n1,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_MemberRankRule',
    filePrefix: 'w2_member_rank_rule_',
    description:
      '会員ランク付与ルール（全40項目）を登録・更新します。member_rank_rule_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'member_rank_rule_id', required: true, desc: 'ランク付与ルールID' },
      { name: 'member_rank_rule_name', required: false, desc: 'ランク付与ルール名' },
      { name: 'status', required: false, desc: 'ステータス' },
      { name: 'last_count', required: false, desc: '最終付与人数' },
      { name: 'last_exec_date', required: false, desc: '最終付与日時' },
      { name: 'target_extract_type', required: false, desc: '抽出条件集計期間指定' },
      { name: 'target_extract_start', required: false, desc: '抽出条件集計期間開始日' },
      { name: 'target_extract_end', required: false, desc: '抽出条件集計期間終了日' },
      { name: 'target_extract_days_ago', required: false, desc: '抽出条件集計期間前日指定' },
      {
        name: 'target_extract_total_price_from',
        required: false,
        desc: '抽出条件合計購入金額範囲(From)'
      },
      {
        name: 'target_extract_total_price_to',
        required: false,
        desc: '抽出条件合計購入金額範囲(To)'
      },
      {
        name: 'target_extract_total_count_from',
        required: false,
        desc: '抽出条件合計購入回数範囲(From)'
      },
      {
        name: 'target_extract_total_count_to',
        required: false,
        desc: '抽出条件合計購入回数範囲(To)'
      },
      {
        name: 'target_extract_old_rank_flg',
        required: false,
        desc: '抽出時の旧ランク情報抽出判定'
      },
      { name: 'rank_change_type', required: false, desc: 'ランク付与方法' },
      { name: 'rank_change_rank_id', required: false, desc: '指定付与ランクID' },
      { name: 'mail_id', required: false, desc: 'メールテンプレートID' },
      { name: 'exec_timing', required: false, desc: '実行タイミング' },
      { name: 'schedule_kbn', required: false, desc: 'スケジュール区分' },
      { name: 'schedule_day_of_week', required: false, desc: 'スケジュール曜日' },
      { name: 'schedule_year', required: false, desc: 'スケジュール日程(年)' },
      { name: 'schedule_month', required: false, desc: 'スケジュール日程(月)' },
      { name: 'schedule_day', required: false, desc: 'スケジュール日程(日)' },
      { name: 'schedule_hour', required: false, desc: 'スケジュール日程(時)' },
      { name: 'schedule_minute', required: false, desc: 'スケジュール日程(分)' },
      { name: 'schedule_second', required: false, desc: 'スケジュール日程(秒)' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'target_id', required: false, desc: 'ターゲットID' },
      { name: 'target_extract_flg', required: false, desc: 'ターゲット抽出フラグ' },
      { name: 'target_id2', required: false, desc: 'ターゲットID2' },
      { name: 'target_extract_flg2', required: false, desc: 'ターゲット2抽出フラグ' },
      { name: 'target_id3', required: false, desc: 'ターゲットID3' },
      { name: 'target_extract_flg3', required: false, desc: 'ターゲット3抽出フラグ' },
      { name: 'target_id4', required: false, desc: 'ターゲットID4' },
      { name: 'target_extract_flg4', required: false, desc: 'ターゲット4抽出フラグ' },
      { name: 'target_id5', required: false, desc: 'ターゲットID5' },
      { name: 'target_extract_flg5', required: false, desc: 'ターゲット5抽出フラグ' }
    ],
    sample:
      'member_rank_rule_id,member_rank_rule_name,status,last_count,last_exec_date,target_extract_type,target_extract_start,target_extract_end\n1,sample,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_OrderShipping',
    filePrefix: 'w2_order_shipping_',
    description:
      '注文配送先情報（全76項目）を登録・更新します。order_id+order_shipping_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'order_id', required: true, desc: '注文ID' },
      { name: 'order_shipping_no', required: true, desc: '配送先枝番' },
      { name: 'shipping_name', required: false, desc: '配送先氏名' },
      { name: 'shipping_name_kana', required: false, desc: '配送先氏名かな' },
      { name: 'shipping_zip', required: false, desc: '郵便番号' },
      { name: 'shipping_addr1', required: false, desc: '住所1' },
      { name: 'shipping_addr2', required: false, desc: '住所2' },
      { name: 'shipping_addr3', required: false, desc: '住所3' },
      { name: 'shipping_addr4', required: false, desc: '住所４' },
      { name: 'shipping_tel1', required: false, desc: '電話番号1' },
      { name: 'shipping_tel2', required: false, desc: '電話番号2' },
      { name: 'shipping_tel3', required: false, desc: '電話番号3' },
      { name: 'shipping_fax', required: false, desc: 'ＦＡＸ' },
      { name: 'shipping_company', required: false, desc: '配送業者' },
      { name: 'shipping_date', required: false, desc: '配送希望日' },
      { name: 'shipping_time', required: false, desc: '配送希望時間帯' },
      { name: 'shipping_check_no', required: false, desc: '配送伝票番号' },
      { name: 'del_flg', required: false, desc: '削除フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'shipping_name1', required: false, desc: '配送先氏名1' },
      { name: 'shipping_name2', required: false, desc: '配送先氏名2' },
      { name: 'shipping_name_kana1', required: false, desc: '配送先氏名かな1' },
      { name: 'shipping_name_kana2', required: false, desc: '配送先氏名かな2' },
      { name: 'sender_name', required: false, desc: '送り主氏名' },
      { name: 'sender_name1', required: false, desc: '送り主氏名1' },
      { name: 'sender_name2', required: false, desc: '送り主氏名2' },
      { name: 'sender_name_kana', required: false, desc: '送り主氏名かな' },
      { name: 'sender_name_kana1', required: false, desc: '送り主氏名かな1' },
      { name: 'sender_name_kana2', required: false, desc: '送り主氏名かな2' },
      { name: 'sender_zip', required: false, desc: '送り主郵便番号' },
      { name: 'sender_addr1', required: false, desc: '送り主住所1' },
      { name: 'sender_addr2', required: false, desc: '送り主住所2' },
      { name: 'sender_addr3', required: false, desc: '送り主住所3' },
      { name: 'sender_addr4', required: false, desc: '送り主住所４' },
      { name: 'sender_tel1', required: false, desc: '送り主電話番号1' },
      { name: 'wrapping_paper_type', required: false, desc: 'のし種類' },
      { name: 'wrapping_paper_name', required: false, desc: 'のし差出人' },
      { name: 'wrapping_bag_type', required: false, desc: '包装種類' },
      { name: 'shipping_company_name', required: false, desc: '配送先企業名' },
      { name: 'shipping_company_post_name', required: false, desc: '配送先部署名' },
      { name: 'sender_company_name', required: false, desc: '送り主企業名' },
      { name: 'sender_company_post_name', required: false, desc: '送り主部署名' },
      { name: 'private_shipping_flg', required: false, desc: 'プライベート配送フラグ' },
      { name: 'another_shipping_flg', required: false, desc: '別送フラグ' },
      { name: 'shipping_method', required: false, desc: '配送方法' },
      { name: 'delivery_company_id', required: false, desc: '配送会社ID' },
      { name: 'shipping_country_iso_code', required: false, desc: '配送先住所国ISOコード' },
      { name: 'shipping_country_name', required: false, desc: '配送先住所国名' },
      { name: 'shipping_addr5', required: false, desc: '住所5' },
      { name: 'sender_country_iso_code', required: false, desc: '送り主住所国ISOコード' },
      { name: 'sender_country_name', required: false, desc: '送り主住所国名' },
      { name: 'sender_addr5', required: false, desc: '送り主住所5' },
      { name: 'scheduled_shipping_date', required: false, desc: '出荷予定日' },
      { name: 'external_shipping_cooperation_id', required: false, desc: '外部連携用ID' },
      { name: 'shipping_receiving_store_flg', required: false, desc: '店舗受取フラグ' },
      { name: 'shipping_receiving_store_id', required: false, desc: '店舗受取店舗ID' },
      {
        name: 'shipping_external_deliverty_status',
        required: false,
        desc: '配送のペリカン管理番号'
      },
      { name: 'shipping_status', required: false, desc: '配送状態' },
      { name: 'shipping_status_update_date', required: false, desc: '配送状態更新日' },
      { name: 'shipping_receiving_mail_date', required: false, desc: '店舗受取メール送信日' },
      { name: 'shipping_receiving_store_type', required: false, desc: '受取方法' },
      { name: 'shipping_status_code', required: false, desc: '完了状態コード' },
      { name: 'shipping_office_name', required: false, desc: '営業所略称' },
      { name: 'shipping_handy_time', required: false, desc: 'Handy操作時間' },
      { name: 'shipping_current_status', required: false, desc: '現在の状態' },
      { name: 'shipping_status_detail', required: false, desc: '状態説明' },
      { name: 'shipping_temperature_zone', required: false, desc: '配送温度帯' },
      { name: 'order_shipping_id', required: false, desc: '注文配送先ID' },
      { name: 'order_shipping_status', required: false, desc: '出荷ステータス' },
      { name: 'order_shipping_arranged_date', required: false, desc: '出荷手配日' },
      { name: 'order_shipping_completed_date', required: false, desc: '出荷完了日' },
      { name: 'wrapping_paper_flg', required: false, desc: 'のし利用' },
      { name: 'wrapping_paper_title', required: false, desc: 'のし表書き' },
      { name: 'wrapping_paper_name_style', required: false, desc: 'のし差出人印字形式' },
      { name: 'wrapping_paper_placement', required: false, desc: 'のし位置' }
    ],
    sample:
      'order_id,order_shipping_no,shipping_name,shipping_name_kana,shipping_zip,shipping_addr1,shipping_addr2,shipping_addr3\n1,1,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_ShopShipping',
    filePrefix: 'w2_shop_shipping_',
    description: '店舗配送種別（全66項目）を登録・更新します。shop_id+shipping_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID' },
      { name: 'shipping_id', required: true, desc: '配送種別ID' },
      { name: 'shop_shipping_name', required: false, desc: '配送種別名' },
      { name: 'payment_relation_id', required: false, desc: '決済連携ID' },
      { name: 'shipping_price_kbn', required: false, desc: '配送料設定区分' },
      { name: 'shipping_date_set_flg', required: false, desc: '配送日設定可能フラグ' },
      { name: 'shipping_date_set_begin', required: false, desc: '配送日設定可能範囲(開始)' },
      { name: 'shipping_date_set_end', required: false, desc: '配送日設定可能範囲（終了）' },
      { name: 'shipping_time_set_flg', required: false, desc: '配送希望時間帯設定可能フラグ' },
      { name: 'shipping_time_id1', required: false, desc: '配送希望時間帯ID1' },
      { name: 'shipping_time_message1', required: false, desc: '配送希望時間帯文言1' },
      { name: 'shipping_time_id2', required: false, desc: '配送希望時間帯ID2' },
      { name: 'shipping_time_message2', required: false, desc: '配送希望時間帯文言2' },
      { name: 'shipping_time_id3', required: false, desc: '配送希望時間帯ID3' },
      { name: 'shipping_time_message3', required: false, desc: '配送希望時間帯文言3' },
      { name: 'shipping_time_id4', required: false, desc: '配送希望時間帯ID4' },
      { name: 'shipping_time_message4', required: false, desc: '配送希望時間帯文言4' },
      { name: 'shipping_time_id5', required: false, desc: '配送希望時間帯ID5' },
      { name: 'shipping_time_message5', required: false, desc: '配送希望時間帯文言5' },
      { name: 'shipping_time_id6', required: false, desc: '配送希望時間帯ID6' },
      { name: 'shipping_time_message6', required: false, desc: '配送希望時間帯文言6' },
      { name: 'shipping_time_id7', required: false, desc: '配送希望時間帯ID7' },
      { name: 'shipping_time_message7', required: false, desc: '配送希望時間帯文言7' },
      { name: 'shipping_time_id8', required: false, desc: '配送希望時間帯ID8' },
      { name: 'shipping_time_message8', required: false, desc: '配送希望時間帯文言8' },
      { name: 'shipping_time_id9', required: false, desc: '配送希望時間帯ID9' },
      { name: 'shipping_time_message9', required: false, desc: '配送希望時間帯文言9' },
      { name: 'shipping_time_id10', required: false, desc: '配送希望時間帯ID10' },
      { name: 'shipping_time_message10', required: false, desc: '配送希望時間帯文言10' },
      { name: 'shipping_free_price_flg', required: false, desc: '配送料無料購入金額設定フラグ' },
      { name: 'shipping_free_price', required: false, desc: '配送料無料購入金額設定' },
      { name: 'announce_free_shipping_flg', required: false, desc: '配送料無料案内表示フラグ' },
      { name: 'calculation_plural_kbn', required: false, desc: '複数商品計算区分' },
      { name: 'plural_shipping_price', required: false, desc: '複数商品配送料' },
      { name: 'del_flg', required: false, desc: '削除フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'fixed_purchase_flg', required: false, desc: '定期購入設定可能フラグ' },
      { name: 'fixed_purchase_kbn1_flg', required: false, desc: '定期購入区分1設定可能フラグ' },
      { name: 'fixed_purchase_kbn1_setting', required: false, desc: '定期購入区分1設定値' },
      { name: 'fixed_purchase_kbn2_flg', required: false, desc: '定期購入区分2設定可能フラグ' },
      { name: 'fixed_purchase_kbn2_setting', required: false, desc: '定期購入区分2設定値' },
      { name: 'fixed_purchase_kbn3_flg', required: false, desc: '定期購入区分3設定可能フラグ' },
      { name: 'fixed_purchase_kbn3_setting', required: false, desc: '定期購入区分3設定値' },
      {
        name: 'fixed_purchase_order_entry_timing',
        required: false,
        desc: '定期購入受注タイミング'
      },
      { name: 'fixed_purchase_cancel_deadline', required: false, desc: '定期購入解約期限' },
      { name: 'wrapping_paper_flg', required: false, desc: 'のし利用フラグ' },
      { name: 'wrapping_paper_types', required: false, desc: 'のし種類' },
      { name: 'wrapping_bag_flg', required: false, desc: '包装利用フラグ' },
      { name: 'wrapping_bag_types', required: false, desc: '包装種類' },
      { name: 'payment_selection_flg', required: false, desc: '決済選択の任意利用フラグ' },
      { name: 'permitted_payment_ids', required: false, desc: '決済選択の可能リスト' },
      {
        name: 'fixed_purchase_shipping_days_required',
        required: false,
        desc: '定期購入配送所要日数'
      },
      {
        name: 'fixed_purchase_minimum_shipping_span',
        required: false,
        desc: '定期購入最低配送間隔（所定日数）'
      },
      { name: 'fixed_purchase_free_shipping_flg', required: false, desc: '定期配送料無料フラグ' },
      { name: 'business_days_for_shipping', required: false, desc: '出荷所要営業日数' },
      {
        name: 'next_shipping_max_change_days',
        required: false,
        desc: '次回配送日の選択可能最大日数'
      },
      {
        name: 'fixed_purchase_shipping_notdisplay_flg',
        required: false,
        desc: '配送パターン非表示フラグ'
      },
      { name: 'fixed_purchase_kbn4_flg', required: false, desc: '定期購入区分4設定可能フラグ' },
      { name: 'fixed_purchase_kbn4_setting1', required: false, desc: '定期購入区分4設定値1' },
      { name: 'fixed_purchase_kbn4_setting2', required: false, desc: '定期購入区分4設定値2' },
      { name: 'fixed_purchase_kbn1_setting2', required: false, desc: '定期購入区分1設定値2' },
      { name: 'shipping_date_required_flg', required: false, desc: '配送希望日が入力必須か' },
      { name: 'wrapping_papers', required: false, desc: 'のし情報' },
      { name: 'wrapping_bags', required: false, desc: '包装情報' }
    ],
    sample:
      'shop_id,shipping_id,shop_shipping_name,payment_relation_id,shipping_price_kbn,shipping_date_set_flg,shipping_date_set_begin,shipping_date_set_end\n1,1,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_ShopShippingZone',
    filePrefix: 'w2_shop_shipping_zone_',
    description:
      '店舗配送料地帯（全22項目）を登録・更新します。shop_id+shipping_id+delivery_company_id+shipping_zone_no+shipping_temperature_zoneでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID' },
      { name: 'shipping_id', required: true, desc: '配送料設定ID' },
      { name: 'delivery_company_id', required: true, desc: '配送会社ID' },
      { name: 'shipping_zone_no', required: true, desc: '配送料地帯区分' },
      { name: 'shipping_zone_name', required: false, desc: '地帯名' },
      { name: 'zip', required: false, desc: '郵便番号' },
      { name: 'size_xxs_shipping_price', required: false, desc: 'XXSサイズ商品配送料' },
      { name: 'size_xs_shipping_price', required: false, desc: 'XSサイズ商品配送料' },
      { name: 'size_s_shipping_price', required: false, desc: 'Sサイズ商品配送料' },
      { name: 'size_m_shipping_price', required: false, desc: 'Mサイズ商品配送料' },
      { name: 'size_l_shipping_price', required: false, desc: 'Lサイズ商品配送料' },
      { name: 'size_xl_shipping_price', required: false, desc: 'XLサイズ商品配送料' },
      { name: 'size_xxl_shipping_price', required: false, desc: 'XXLサイズ商品配送料' },
      { name: 'del_flg', required: false, desc: '削除フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'size_mail_shipping_price', required: false, desc: 'メールサイズ商品配送料' },
      { name: 'conditional_shipping_price_threshold', required: false, desc: '条件付き配送料閾値' },
      { name: 'conditional_shipping_price', required: false, desc: '条件付き配送料' },
      { name: 'unavailable_shipping_area_flg', required: false, desc: '配送不可エリアフラグ' },
      { name: 'shipping_temperature_zone', required: true, desc: '配送温度帯' }
    ],
    sample:
      'shop_id,shipping_id,delivery_company_id,shipping_zone_no,shipping_zone_name,zip,size_xxs_shipping_price,size_xs_shipping_price\n1,1,1,1,sample,sample,sample,sample'
  },
  {
    type: 'w2_ShopShippingSizeThreshold',
    filePrefix: 'w2_shop_shipping_size_threshold_',
    description:
      '配送サイズ別上限設定（全13項目）を登録・更新します。shop_id+shipping_id+delivery_company_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID' },
      { name: 'shipping_id', required: true, desc: '配送種別ID' },
      { name: 'delivery_company_id', required: true, desc: '配送会社ID' },
      { name: 'size_xxs_threshold', required: false, desc: 'XXS配送区分上限' },
      { name: 'size_xs_threshold', required: false, desc: 'XS配送区分上限' },
      { name: 'size_s_threshold', required: false, desc: 'S配送区分上限' },
      { name: 'size_m_threshold', required: false, desc: 'M配送区分上限' },
      { name: 'size_l_threshold', required: false, desc: 'L配送区分上限' },
      { name: 'size_xl_threshold', required: false, desc: 'XL配送区分上限' },
      { name: 'size_xxl_threshold', required: false, desc: 'XXL配送区分上限' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'shop_id,shipping_id,delivery_company_id,size_xxs_threshold,size_xs_threshold,size_s_threshold,size_m_threshold,size_l_threshold\n1,1,1,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_MailDistSetting',
    filePrefix: 'w2_mail_dist_setting_',
    description: 'メール配信設定（全33項目）を登録・更新します。dept_id+maildist_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'dept_id', required: true, desc: '識別ID' },
      { name: 'maildist_id', required: true, desc: 'メール配信設定ID' },
      { name: 'maildist_name', required: false, desc: 'メール配信設定名' },
      { name: 'status', required: false, desc: 'ステータス' },
      { name: 'last_count', required: false, desc: '最終集計人数' },
      { name: 'last_errorexcept_count', required: false, desc: '最終エラー除外人数' },
      { name: 'last_dist_date', required: false, desc: '最終配信開始日時' },
      { name: 'target_id', required: false, desc: 'ターゲットID' },
      { name: 'target_extract_flg', required: false, desc: 'ターゲット抽出フラグ' },
      { name: 'target_id2', required: false, desc: 'ターゲットID2' },
      { name: 'target_extract_flg2', required: false, desc: 'ターゲット2抽出フラグ' },
      { name: 'target_id3', required: false, desc: 'ターゲットID3' },
      { name: 'target_extract_flg3', required: false, desc: 'ターゲット3抽出フラグ' },
      { name: 'target_id4', required: false, desc: 'ターゲットID4' },
      { name: 'target_extract_flg4', required: false, desc: 'ターゲット4抽出フラグ' },
      { name: 'target_id5', required: false, desc: 'ターゲットID5' },
      { name: 'target_extract_flg5', required: false, desc: 'ターゲット5抽出フラグ' },
      { name: 'except_error_point', required: false, desc: '配信除外エラーポイント' },
      { name: 'mailtext_id', required: false, desc: 'メール文章ID' },
      { name: 'exec_timing', required: false, desc: '実行タイミング' },
      { name: 'schedule_kbn', required: false, desc: 'スケジュール区分' },
      { name: 'schedule_day_of_week', required: false, desc: 'スケジュール曜日' },
      { name: 'schedule_year', required: false, desc: 'スケジュール日程(年)' },
      { name: 'schedule_month', required: false, desc: 'スケジュール日程(月)' },
      { name: 'schedule_day', required: false, desc: 'スケジュール日程(日)' },
      { name: 'schedule_hour', required: false, desc: 'スケジュール日程(時)' },
      { name: 'schedule_minute', required: false, desc: 'スケジュール日程(分)' },
      { name: 'schedule_second', required: false, desc: 'スケジュール日程(秒)' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'del_flg', required: false, desc: '削除フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'dept_id,maildist_id,maildist_name,status,last_count,last_errorexcept_count,last_dist_date,target_id\n1,1,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_MailDistSentUser',
    filePrefix: 'w2_mail_dist_sent_user_',
    description:
      'メール配信送信済ユーザ（全5項目）を登録・更新します。maildist_id+user_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'maildist_id', required: true, desc: 'メール配信設定ID' },
      { name: 'user_id', required: true, desc: 'ユーザID' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample: 'maildist_id,user_id,date_created,date_changed,last_changed\n1,1,sample,sample,sample'
  },
  {
    type: 'w2_MailSendLog',
    filePrefix: 'w2_mail_send_log_',
    description: 'メール送信ログ（全24項目）を登録・更新します。log_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'log_no', required: true, desc: 'ログNO' },
      { name: 'user_id', required: false, desc: 'ユーザーID' },
      { name: 'dept_id', required: false, desc: '識別ID' },
      { name: 'mailtext_id', required: false, desc: 'メール文章ID' },
      { name: 'mailtext_name', required: false, desc: 'メール文章名' },
      { name: 'maildist_id', required: false, desc: 'メール配信設定ID' },
      { name: 'maildist_name', required: false, desc: 'メール配信設定名' },
      { name: 'shop_id', required: false, desc: '店舗ID' },
      { name: 'mail_id', required: false, desc: 'メールテンプレートID' },
      { name: 'mail_name', required: false, desc: 'メールテンプレート名' },
      { name: 'mail_from_name', required: false, desc: '送信元名' },
      { name: 'mail_from', required: false, desc: 'メールFrom' },
      { name: 'mail_to', required: false, desc: 'メールTo' },
      { name: 'mail_cc', required: false, desc: 'メールCC' },
      { name: 'mail_bcc', required: false, desc: 'メールBCC' },
      { name: 'mail_subject', required: false, desc: 'メールタイトル' },
      { name: 'mail_body', required: false, desc: 'メール本文' },
      { name: 'mail_body_html', required: false, desc: 'メール本文HTML' },
      { name: 'error_message', required: false, desc: 'エラーメッセージ' },
      { name: 'date_send_mail', required: false, desc: '送信日時' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'read_flg', required: false, desc: '既読フラグ' },
      { name: 'date_read_mail', required: false, desc: '既読日' },
      { name: 'mail_addr_kbn', required: false, desc: 'メールアドレス区分' }
    ],
    sample:
      'log_no,user_id,dept_id,mailtext_id,mailtext_name,maildist_id,maildist_name,shop_id\n1,sample,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_MailClick',
    filePrefix: 'w2_mail_click_',
    description:
      'メールクリック（全13項目）を登録・更新します。dept_id+mailtext_id+maildist_id+action_no+mailclick_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'dept_id', required: true, desc: '識別ID' },
      { name: 'mailtext_id', required: true, desc: 'メール文章ID' },
      { name: 'maildist_id', required: true, desc: 'メール配信設定ID' },
      { name: 'action_no', required: true, desc: '実行履歴NO' },
      { name: 'mailclick_id', required: true, desc: 'メールクリックID' },
      { name: 'mailclick_url', required: false, desc: 'メールクリックURL' },
      { name: 'mailclick_key', required: false, desc: 'メールクリック識別キー' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'del_flg', required: false, desc: '削除フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'mail_body_type', required: false, desc: 'メール文章区分' }
    ],
    sample:
      'dept_id,mailtext_id,maildist_id,action_no,mailclick_id,mailclick_url,mailclick_key,valid_flg\n1,1,1,1,1,sample,sample,sample'
  },
  {
    type: 'w2_MailClickLog',
    filePrefix: 'w2_mail_click_log_',
    description: 'メールクリックログ（全8項目）を登録・更新します。log_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'log_no', required: true, desc: 'ログNO' },
      { name: 'dept_id', required: false, desc: '識別ID' },
      { name: 'mailtext_id', required: false, desc: 'メール文章ID' },
      { name: 'maildist_id', required: false, desc: 'メール配信設定ID' },
      { name: 'action_no', required: false, desc: '実行履歴NO' },
      { name: 'mailclick_id', required: false, desc: 'メールクリックID' },
      { name: 'user_id', required: false, desc: 'ユーザーID' },
      { name: 'date_created', required: false, desc: '作成日' }
    ],
    sample:
      'log_no,dept_id,mailtext_id,maildist_id,action_no,mailclick_id,user_id,date_created\n1,sample,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_OrderWorkflowScenarioSetting',
    filePrefix: 'w2_order_workflow_scenario_setting_',
    description:
      '受注ワークフローシナリオ設定（全15項目）を登録・更新します。scenario_setting_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'scenario_setting_id', required: true, desc: 'シナリオ設定ID' },
      { name: 'scenario_name', required: false, desc: 'シナリオ名' },
      { name: 'exec_timing', required: false, desc: '実行タイミング' },
      { name: 'schedule_kbn', required: false, desc: 'スケジュール区分' },
      { name: 'schedule_day_of_week', required: false, desc: 'スケジュール曜日' },
      { name: 'schedule_year', required: false, desc: 'スケジュール日程(年)' },
      { name: 'schedule_month', required: false, desc: 'スケジュール日程(月)' },
      { name: 'schedule_day', required: false, desc: 'スケジュール日程(日)' },
      { name: 'schedule_hour', required: false, desc: 'スケジュール日程(時)' },
      { name: 'schedule_minute', required: false, desc: 'スケジュール日程(分)' },
      { name: 'schedule_second', required: false, desc: 'スケジュール日程(秒)' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'scenario_setting_id,scenario_name,exec_timing,schedule_kbn,schedule_day_of_week,schedule_year,schedule_month,schedule_day\n1,sample,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_Novelty',
    filePrefix: 'w2_novelty_',
    description: 'ノベルティ設定（全11項目）を登録・更新します。shop_id+novelty_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID' },
      { name: 'novelty_id', required: true, desc: 'ノベルティID' },
      { name: 'novelty_disp_name', required: false, desc: 'ノベルティ名（表示用）' },
      { name: 'novelty_name', required: false, desc: 'ノベルティ名（管理用）' },
      { name: 'discription', required: false, desc: '説明（管理用）' },
      { name: 'date_begin', required: false, desc: '開始日時' },
      { name: 'date_end', required: false, desc: '終了日時' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'shop_id,novelty_id,novelty_disp_name,novelty_name,discription,date_begin,date_end,valid_flg\n1,1,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_NoveltyGrantConditions',
    filePrefix: 'w2_novelty_grant_conditions_',
    description:
      'ノベルティ付与条件（全9項目）を登録・更新します。shop_id+novelty_id+condition_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID' },
      { name: 'novelty_id', required: true, desc: 'ノベルティID' },
      { name: 'condition_no', required: true, desc: 'ノベルティ付与条件枝番' },
      { name: 'user_rank_id', required: false, desc: '対象会員' },
      { name: 'amount_begin', required: false, desc: '対象金額（以上）' },
      { name: 'amount_end', required: false, desc: '対象金額（以下）' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'shop_id,novelty_id,condition_no,user_rank_id,amount_begin,amount_end,date_created,date_changed\n1,1,1,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_NoveltyGrantItem',
    filePrefix: 'w2_novelty_grant_item_',
    description:
      'ノベルティ付与アイテム（全8項目）を登録・更新します。shop_id+novelty_id+condition_no+product_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID' },
      { name: 'novelty_id', required: true, desc: 'ノベルティID' },
      { name: 'condition_no', required: true, desc: 'ノベルティ付与条件枝番' },
      { name: 'product_id', required: true, desc: '商品ID' },
      { name: 'sort_no', required: false, desc: '並び順' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'shop_id,novelty_id,condition_no,product_id,sort_no,date_created,date_changed,last_changed\n1,1,1,1,sample,sample,sample,sample'
  },
  {
    type: 'w2_NoveltyTargetItem',
    filePrefix: 'w2_novelty_target_item_',
    description:
      'ノベルティ対象アイテム（全9項目）を登録・更新します。shop_id+novelty_id+novelty_target_item_no+novelty_target_item_type+novelty_target_item_valueでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID' },
      { name: 'novelty_id', required: true, desc: 'ノベルティID' },
      { name: 'novelty_target_item_no', required: true, desc: 'ノベルティ対象アイテム枝番' },
      { name: 'novelty_target_item_type', required: true, desc: 'ノベルティ対象アイテム種別' },
      { name: 'novelty_target_item_value', required: true, desc: 'ノベルティ対象アイテム値' },
      {
        name: 'novelty_target_item_type_sort_no',
        required: false,
        desc: 'ノベルティ対象アイテム並び順'
      },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'shop_id,novelty_id,novelty_target_item_no,novelty_target_item_type,novelty_target_item_value,novelty_target_item_type_sort_no,date_created,date_changed\n1,1,1,1,1,sample,sample,sample'
  },
  {
    type: 'w2_OrderSetPromotion',
    filePrefix: 'w2_order_setpromotion_',
    description:
      '注文セットプロモーションテーブル（全7項目）を登録・更新します。order_id+order_setpromotion_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'order_id', required: true, desc: '注文ID' },
      { name: 'order_setpromotion_no', required: true, desc: '注文セットプロモーション枝番' },
      { name: 'setpromotion_id', required: false, desc: 'セットプロモーションID' },
      { name: 'setpromotion_name', required: false, desc: '管理用セットプロモーション名' },
      { name: 'setpromotion_disp_name', required: false, desc: '表示用セットプロモーション名' },
      { name: 'promotion_type', required: false, desc: 'プロモーション種別' },
      { name: 'discount_amount', required: false, desc: '値引き額' }
    ],
    sample:
      'order_id,order_setpromotion_no,setpromotion_id,setpromotion_name,setpromotion_disp_name,promotion_type,discount_amount\n1,1,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_OrderSetPromotionItem',
    filePrefix: 'w2_order_setpromotion_item_',
    description:
      '注文セットプロモーションアイテム（全5項目）を登録・更新します。order_id+order_setpromotion_no+order_setpromotion_item_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'order_id', required: true, desc: '注文ID' },
      { name: 'order_setpromotion_no', required: true, desc: '注文セットプロモーション枝番' },
      {
        name: 'order_setpromotion_item_no',
        required: true,
        desc: '注文セットプロモーションアイテム枝番'
      },
      { name: 'order_item_no', required: false, desc: '注文商品枝番' },
      { name: 'order_item_quantity', required: false, desc: '注文商品該当数' }
    ],
    sample:
      'order_id,order_setpromotion_no,order_setpromotion_item_no,order_item_no,order_item_quantity\n1,1,1,sample,sample'
  },
  {
    type: 'w2_SetPromotion',
    filePrefix: 'w2_setpromotion_',
    description:
      'セットプロモーションマスター（全20項目）を登録・更新します。setpromotion_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'setpromotion_id', required: true, desc: 'セットプロモーションID' },
      { name: 'setpromotion_name', required: false, desc: '管理用セットプロモーション名' },
      { name: 'setpromotion_disp_name', required: false, desc: '表示用セットプロモーション名' },
      { name: 'promotion_type', required: false, desc: 'プロモーション種別' },
      { name: 'product_discount_kbn', required: false, desc: '商品割引区分' },
      { name: 'product_discount_setting', required: false, desc: '商品割引設定' },
      { name: 'multiple_apply_flg', required: false, desc: '重複適用フラグ' },
      { name: 'apply_order', required: false, desc: '適用優先順' },
      { name: 'description_kbn', required: false, desc: '表示用文言HTML区分' },
      { name: 'description', required: false, desc: '表示用文言' },
      { name: 'begin_date', required: false, desc: '開始日時' },
      { name: 'end_date', required: false, desc: '終了日時' },
      { name: 'target_member_rank', required: false, desc: '適用会員ランク' },
      { name: 'target_target_ids', required: false, desc: '適用ターゲットリスト' },
      { name: 'url', required: false, desc: 'URL' },
      { name: 'display_order', required: false, desc: '表示優先順' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'setpromotion_id,setpromotion_name,setpromotion_disp_name,promotion_type,product_discount_kbn,product_discount_setting,multiple_apply_flg,apply_order\n1,sample,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_SetPromotionItem',
    filePrefix: 'w2_setpromotion_item_',
    description:
      'セットプロモーション商品（全6項目）を登録・更新します。setpromotion_id+setpromotion_item_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'setpromotion_id', required: true, desc: 'セットプロモーションID' },
      { name: 'setpromotion_item_no', required: true, desc: 'セットプロモーションアイテム枝番' },
      { name: 'setpromotion_item_kbn', required: false, desc: 'セットプロモーションアイテム区分' },
      { name: 'setpromotion_items', required: false, desc: '対象商品' },
      { name: 'setpromotion_item_quantity', required: false, desc: '数量' },
      { name: 'and_over_quantity_flg', required: false, desc: 'n個以上フラグ' }
    ],
    sample:
      'setpromotion_id,setpromotion_item_no,setpromotion_item_kbn,setpromotion_items,setpromotion_item_quantity,and_over_quantity_flg\n1,1,sample,sample,sample,sample'
  },
  {
    type: 'w2_ProductBundle',
    filePrefix: 'w2_product_bundle_',
    description: '商品同梱（全29項目）を登録・更新します。product_bundle_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'product_bundle_id', required: true, desc: '商品同梱ID' },
      { name: 'product_bundle_name', required: false, desc: '商品同梱名' },
      { name: 'target_order_type', required: false, desc: '対象注文種別' },
      { name: 'description', required: false, desc: '説明文' },
      { name: 'start_datetime', required: false, desc: '開始日時' },
      { name: 'end_datetime', required: false, desc: '終了日時' },
      { name: 'target_product_kbn', required: false, desc: '対象購入商品指定方法' },
      { name: 'target_product_ids', required: false, desc: '対象購入商品ID' },
      {
        name: 'target_order_fixed_purchase_count_from',
        required: false,
        desc: '対象定期注文回数_FROM'
      },
      {
        name: 'target_order_fixed_purchase_count_to',
        required: false,
        desc: '対象定期注文回数_TO'
      },
      { name: 'usable_times_kbn', required: false, desc: 'ユーザ利用可能回数' },
      { name: 'apply_type', required: false, desc: '商品同梱設定適用種別' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'multiple_apply_flg', required: false, desc: '重複適用フラグ' },
      { name: 'apply_order', required: false, desc: '適用優先順' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'usable_times', required: false, desc: 'ユーザ利用可能指定回数' },
      { name: 'target_product_category_ids', required: false, desc: '対象購入商品カテゴリID' },
      { name: 'except_product_ids', required: false, desc: '対象外購入商品ID' },
      { name: 'except_product_category_ids', required: false, desc: '対象外購入商品カテゴリID' },
      { name: 'target_id', required: false, desc: 'ターゲットリストID' },
      { name: 'target_id_except_flg', required: false, desc: 'ターゲットリストID除外フラグ' },
      {
        name: 'target_order_price_subtotal_min',
        required: false,
        desc: '対象注文商品合計金額適用下限'
      },
      { name: 'target_product_count_min', required: false, desc: '対象商品個数適用下限' },
      { name: 'target_advcodes_first', required: false, desc: '初回広告コード' },
      { name: 'target_advcodes_new', required: false, desc: '最新広告コード' },
      { name: 'target_payment_ids', required: false, desc: '対象決済種別ID' }
    ],
    sample:
      'product_bundle_id,product_bundle_name,target_order_type,description,start_datetime,end_datetime,target_product_kbn,target_product_ids\n1,sample,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_ProductBundleItem',
    filePrefix: 'w2_product_bundle_item_',
    description:
      '商品同梱 同梱商品（全7項目）を登録・更新します。product_bundle_id+product_bundle_item_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'product_bundle_id', required: true, desc: '商品同梱ID' },
      { name: 'product_bundle_item_no', required: true, desc: '同梱商品枝番' },
      { name: 'grant_product_id', required: false, desc: '同梱商品ID' },
      { name: 'grant_product_variation_id', required: false, desc: '同梱商品バリエーションID' },
      { name: 'grant_product_count', required: false, desc: '同梱個数' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'product_bundle_id,product_bundle_item_no,grant_product_id,grant_product_variation_id,grant_product_count,date_created,last_changed\n1,1,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_ProductSet',
    filePrefix: 'w2_product_set_',
    description: '商品セット（全17項目）を登録・更新します。shop_id+product_set_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID' },
      { name: 'product_set_id', required: true, desc: '商品セットID' },
      { name: 'product_set_name', required: false, desc: '商品セット名' },
      { name: 'parent_min', required: false, desc: '親商品数(下限)' },
      { name: 'parent_max', required: false, desc: '親商品数(上限)' },
      { name: 'child_min', required: false, desc: '子商品数(下限)' },
      { name: 'child_max', required: false, desc: '子商品数(上限)' },
      { name: 'priority', required: false, desc: '適用優先順位' },
      { name: 'description_kbn', required: false, desc: '表示用文言HTML区分' },
      { name: 'description', required: false, desc: '表示用文言' },
      { name: 'max_sell_quantity', required: false, desc: '販売可能数' },
      { name: 'shipping_size_kbn', required: false, desc: '配送サイズ区分' },
      { name: 'editable_flg', required: false, desc: '編集可能フラグ' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'shop_id,product_set_id,product_set_name,parent_min,parent_max,child_min,child_max,priority\n1,1,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_ProductSetItem',
    filePrefix: 'w2_product_set_item_',
    description:
      '商品セットアイテム （全11項目）を登録・更新します。shop_id+product_set_id+product_id+variation_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID' },
      { name: 'product_set_id', required: true, desc: '商品セットID' },
      { name: 'product_id', required: true, desc: '商品ID' },
      { name: 'variation_id', required: true, desc: '商品バリエーションID' },
      { name: 'setitem_price', required: false, desc: 'セット価格' },
      { name: 'count_min', required: false, desc: '個数（下限）' },
      { name: 'count_max', required: false, desc: '個数（上限）' },
      { name: 'family_flg', required: false, desc: '親子フラグ' },
      { name: 'display_order', required: false, desc: '表示順' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'shop_id,product_set_id,product_id,variation_id,setitem_price,count_min,count_max,family_flg\n1,1,1,1,sample,sample,sample,sample'
  },
  {
    type: 'w2_ProductSale',
    filePrefix: 'w2_product_sale_',
    description: '商品セール（全13項目）を登録・更新します。shop_id+productsale_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID' },
      { name: 'productsale_id', required: true, desc: '商品セールID' },
      { name: 'productsale_kbn', required: false, desc: '商品セール区分' },
      { name: 'productsale_name', required: false, desc: '商品セール名' },
      { name: 'closedmarket_password', required: false, desc: 'やみ市パスワード' },
      { name: 'date_bgn', required: false, desc: '開始日時' },
      { name: 'date_end', required: false, desc: '終了日時' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'del_flg', required: false, desc: '削除フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'promotion_stackable_flag', required: false, desc: 'プロモーション同時適用フラグ' }
    ],
    sample:
      'shop_id,productsale_id,productsale_kbn,productsale_name,closedmarket_password,date_bgn,date_end,valid_flg\n1,1,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_ProductSalePrice',
    filePrefix: 'w2_product_sale_price_',
    description:
      '商品セール価格（全10項目）を登録・更新します。shop_id+productsale_id+product_id+variation_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'shop_id', required: true, desc: '店舗ID' },
      { name: 'productsale_id', required: true, desc: '商品セールID' },
      { name: 'product_id', required: true, desc: '商品ID' },
      { name: 'variation_id', required: true, desc: '商品バリエーションID' },
      { name: 'sale_price', required: false, desc: '商品セール価格' },
      { name: 'display_order', required: false, desc: '表示順' },
      { name: 'del_flg', required: false, desc: '削除フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'shop_id,productsale_id,product_id,variation_id,sale_price,display_order,del_flg,date_created\n1,1,1,1,sample,sample,sample,sample'
  },
  {
    type: 'w2_FixedPurchase',
    filePrefix: 'w2_fixed_purchase_',
    description: '定期購入情報（全159項目）を登録・更新します。fixed_purchase_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'fixed_purchase_id', required: true, desc: '定期購入ID' },
      { name: 'fixed_purchase_kbn', required: false, desc: '定期購入区分' },
      { name: 'fixed_purchase_setting1', required: false, desc: '定期購入設定１' },
      { name: 'base_tel_no', required: false, desc: '元電話番号' },
      { name: 'fixed_purchase_status', required: false, desc: '定期購入ステータス' },
      { name: 'payment_status', required: false, desc: '決済ステータス' },
      { name: 'last_order_date', required: false, desc: '最終購入日' },
      { name: 'order_count', required: false, desc: '購入回数(注文基準)' },
      { name: 'user_id', required: false, desc: 'ユーザID' },
      { name: 'shop_id', required: false, desc: '店舗ID' },
      { name: 'supplier_id', required: false, desc: 'サプライヤID' },
      { name: 'order_kbn', required: false, desc: '注文区分' },
      { name: 'order_payment_kbn', required: false, desc: '支払区分' },
      { name: 'fixed_purchase_date_bgn', required: false, desc: '定期購入開始日時' },
      { name: 'card_kbn', required: false, desc: '決済カード区分' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'del_flg', required: false, desc: '削除フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'credit_branch_no', required: false, desc: 'クレジットカード枝番' },
      { name: 'next_shipping_date', required: false, desc: '次回配送日' },
      { name: 'next_next_shipping_date', required: false, desc: '次々回配送日' },
      { name: 'fixed_purchase_management_memo', required: false, desc: '定期購入管理メモ' },
      { name: 'card_installments_code', required: false, desc: 'カード支払い回数コード' },
      { name: 'extend_status1', required: false, desc: '拡張ステータス1' },
      { name: 'extend_status2', required: false, desc: '拡張ステータス2' },
      { name: 'extend_status3', required: false, desc: '拡張ステータス3' },
      { name: 'extend_status4', required: false, desc: '拡張ステータス4' },
      { name: 'extend_status5', required: false, desc: '拡張ステータス5' },
      { name: 'extend_status6', required: false, desc: '拡張ステータス6' },
      { name: 'extend_status7', required: false, desc: '拡張ステータス7' },
      { name: 'extend_status8', required: false, desc: '拡張ステータス8' },
      { name: 'extend_status9', required: false, desc: '拡張ステータス9' },
      { name: 'extend_status10', required: false, desc: '拡張ステータス10' },
      { name: 'extend_status11', required: false, desc: '拡張ステータス11' },
      { name: 'extend_status12', required: false, desc: '拡張ステータス12' },
      { name: 'extend_status13', required: false, desc: '拡張ステータス13' },
      { name: 'extend_status14', required: false, desc: '拡張ステータス14' },
      { name: 'extend_status15', required: false, desc: '拡張ステータス15' },
      { name: 'extend_status16', required: false, desc: '拡張ステータス16' },
      { name: 'extend_status17', required: false, desc: '拡張ステータス17' },
      { name: 'extend_status18', required: false, desc: '拡張ステータス18' },
      { name: 'extend_status19', required: false, desc: '拡張ステータス19' },
      { name: 'extend_status20', required: false, desc: '拡張ステータス20' },
      { name: 'extend_status21', required: false, desc: '拡張ステータス21' },
      { name: 'extend_status22', required: false, desc: '拡張ステータス22' },
      { name: 'extend_status23', required: false, desc: '拡張ステータス23' },
      { name: 'extend_status24', required: false, desc: '拡張ステータス24' },
      { name: 'extend_status25', required: false, desc: '拡張ステータス25' },
      { name: 'extend_status26', required: false, desc: '拡張ステータス26' },
      { name: 'extend_status27', required: false, desc: '拡張ステータス27' },
      { name: 'extend_status28', required: false, desc: '拡張ステータス28' },
      { name: 'extend_status29', required: false, desc: '拡張ステータス29' },
      { name: 'extend_status30', required: false, desc: '拡張ステータス30' },
      { name: 'cancel_reason_id', required: false, desc: '解約理由区分ID' },
      { name: 'cancel_memo', required: false, desc: '解約メモ' },
      { name: 'shipped_count', required: false, desc: '購入回数(出荷基準)' },
      { name: 'extend_status31', required: false, desc: '拡張ステータス31' },
      { name: 'extend_status32', required: false, desc: '拡張ステータス32' },
      { name: 'extend_status33', required: false, desc: '拡張ステータス33' },
      { name: 'extend_status34', required: false, desc: '拡張ステータス34' },
      { name: 'extend_status35', required: false, desc: '拡張ステータス35' },
      { name: 'extend_status36', required: false, desc: '拡張ステータス36' },
      { name: 'extend_status37', required: false, desc: '拡張ステータス37' },
      { name: 'extend_status38', required: false, desc: '拡張ステータス38' },
      { name: 'extend_status39', required: false, desc: '拡張ステータス39' },
      { name: 'extend_status40', required: false, desc: '拡張ステータス40' },
      { name: 'combined_org_fixedpurchase_ids', required: false, desc: '定期購入同梱元定期購入ID' },
      { name: 'access_country_iso_code', required: false, desc: 'アクセス国ISOコード' },
      { name: 'disp_language_code', required: false, desc: '表示言語コード' },
      { name: 'disp_language_locale_id', required: false, desc: '表示言語ロケールID' },
      { name: 'disp_currency_code', required: false, desc: '表示通貨コード' },
      { name: 'disp_currency_locale_id', required: false, desc: '表示通貨ロケールID' },
      { name: 'external_payment_agreement_id', required: false, desc: '外部支払契約ID' },
      { name: 'resume_date', required: false, desc: '定期再開予定日' },
      { name: 'suspend_reason', required: false, desc: '休止理由' },
      { name: 'shipping_memo', required: false, desc: '配送メモ' },
      { name: 'extend_status_date1', required: false, desc: '拡張ステータス更新日1' },
      { name: 'extend_status_date2', required: false, desc: '拡張ステータス更新日2' },
      { name: 'extend_status_date3', required: false, desc: '拡張ステータス更新日3' },
      { name: 'extend_status_date4', required: false, desc: '拡張ステータス更新日4' },
      { name: 'extend_status_date5', required: false, desc: '拡張ステータス更新日5' },
      { name: 'extend_status_date6', required: false, desc: '拡張ステータス更新日6' },
      { name: 'extend_status_date7', required: false, desc: '拡張ステータス更新日7' },
      { name: 'extend_status_date8', required: false, desc: '拡張ステータス更新日8' },
      { name: 'extend_status_date9', required: false, desc: '拡張ステータス更新日9' },
      { name: 'extend_status_date10', required: false, desc: '拡張ステータス更新日10' },
      { name: 'extend_status_date11', required: false, desc: '拡張ステータス更新日11' },
      { name: 'extend_status_date12', required: false, desc: '拡張ステータス更新日12' },
      { name: 'extend_status_date13', required: false, desc: '拡張ステータス更新日13' },
      { name: 'extend_status_date14', required: false, desc: '拡張ステータス更新日14' },
      { name: 'extend_status_date15', required: false, desc: '拡張ステータス更新日15' },
      { name: 'extend_status_date16', required: false, desc: '拡張ステータス更新日16' },
      { name: 'extend_status_date17', required: false, desc: '拡張ステータス更新日17' },
      { name: 'extend_status_date18', required: false, desc: '拡張ステータス更新日18' },
      { name: 'extend_status_date19', required: false, desc: '拡張ステータス更新日19' },
      { name: 'extend_status_date20', required: false, desc: '拡張ステータス更新日20' },
      { name: 'extend_status_date21', required: false, desc: '拡張ステータス更新日21' },
      { name: 'extend_status_date22', required: false, desc: '拡張ステータス更新日22' },
      { name: 'extend_status_date23', required: false, desc: '拡張ステータス更新日23' },
      { name: 'extend_status_date24', required: false, desc: '拡張ステータス更新日24' },
      { name: 'extend_status_date25', required: false, desc: '拡張ステータス更新日25' },
      { name: 'extend_status_date26', required: false, desc: '拡張ステータス更新日26' },
      { name: 'extend_status_date27', required: false, desc: '拡張ステータス更新日27' },
      { name: 'extend_status_date28', required: false, desc: '拡張ステータス更新日28' },
      { name: 'extend_status_date29', required: false, desc: '拡張ステータス更新日29' },
      { name: 'extend_status_date30', required: false, desc: '拡張ステータス更新日30' },
      { name: 'extend_status_date31', required: false, desc: '拡張ステータス更新日31' },
      { name: 'extend_status_date32', required: false, desc: '拡張ステータス更新日32' },
      { name: 'extend_status_date33', required: false, desc: '拡張ステータス更新日33' },
      { name: 'extend_status_date34', required: false, desc: '拡張ステータス更新日34' },
      { name: 'extend_status_date35', required: false, desc: '拡張ステータス更新日35' },
      { name: 'extend_status_date36', required: false, desc: '拡張ステータス更新日36' },
      { name: 'extend_status_date37', required: false, desc: '拡張ステータス更新日37' },
      { name: 'extend_status_date38', required: false, desc: '拡張ステータス更新日38' },
      { name: 'extend_status_date39', required: false, desc: '拡張ステータス更新日39' },
      { name: 'extend_status_date40', required: false, desc: '拡張ステータス更新日40' },
      { name: 'cancel_date', required: false, desc: '解約日' },
      { name: 'restart_date', required: false, desc: '再開日' },
      { name: 'memo', required: false, desc: 'メモ' },
      { name: 'next_shipping_use_coupon_id', required: false, desc: '次回購入の利用クーポンID' },
      { name: 'next_shipping_use_coupon_no', required: false, desc: '次回購入の利用クーポン枝番' },
      { name: 'receipt_flg', required: false, desc: '領収書希望フラグ' },
      { name: 'receipt_address', required: false, desc: '宛名' },
      { name: 'receipt_proviso', required: false, desc: '但し書き' },
      { name: 'skipped_count', required: false, desc: 'スキップ回数' },
      { name: 'extend_status41', required: false, desc: '拡張ステータス41' },
      { name: 'extend_status42', required: false, desc: '拡張ステータス42' },
      { name: 'extend_status43', required: false, desc: '拡張ステータス43' },
      { name: 'extend_status44', required: false, desc: '拡張ステータス44' },
      { name: 'extend_status45', required: false, desc: '拡張ステータス45' },
      { name: 'extend_status46', required: false, desc: '拡張ステータス46' },
      { name: 'extend_status47', required: false, desc: '拡張ステータス47' },
      { name: 'extend_status48', required: false, desc: '拡張ステータス48' },
      { name: 'extend_status49', required: false, desc: '拡張ステータス49' },
      { name: 'extend_status50', required: false, desc: '拡張ステータス50' },
      { name: 'extend_status_date41', required: false, desc: '拡張ステータス更新日時41' },
      { name: 'extend_status_date42', required: false, desc: '拡張ステータス更新日時42' },
      { name: 'extend_status_date43', required: false, desc: '拡張ステータス更新日時43' },
      { name: 'extend_status_date44', required: false, desc: '拡張ステータス更新日時44' },
      { name: 'extend_status_date45', required: false, desc: '拡張ステータス更新日時45' },
      { name: 'extend_status_date46', required: false, desc: '拡張ステータス更新日時46' },
      { name: 'extend_status_date47', required: false, desc: '拡張ステータス更新日時47' },
      { name: 'extend_status_date48', required: false, desc: '拡張ステータス更新日時48' },
      { name: 'extend_status_date49', required: false, desc: '拡張ステータス更新日時49' },
      { name: 'extend_status_date50', required: false, desc: '拡張ステータス更新日時50' },
      { name: 'attribute1', required: false, desc: '注文拡張項目1' },
      { name: 'attribute2', required: false, desc: '注文拡張項目2' },
      { name: 'attribute3', required: false, desc: '注文拡張項目3' },
      { name: 'attribute4', required: false, desc: '注文拡張項目4' },
      { name: 'attribute5', required: false, desc: '注文拡張項目5' },
      { name: 'attribute6', required: false, desc: '注文拡張項目6' },
      { name: 'attribute7', required: false, desc: '注文拡張項目7' },
      { name: 'attribute8', required: false, desc: '注文拡張項目8' },
      { name: 'attribute9', required: false, desc: '注文拡張項目9' },
      { name: 'attribute10', required: false, desc: '注文拡張項目10' },
      { name: 'use_all_point_flg', required: false, desc: '次回購入の利用ポイントの全適用フラグ' },
      { name: 'next_use_point_amount', required: false, desc: '次回購入の利用ポイント数' }
    ],
    sample:
      'fixed_purchase_id,fixed_purchase_kbn,fixed_purchase_setting1,base_tel_no,fixed_purchase_status,payment_status,last_order_date,order_count\n1,sample,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_FixedPurchaseItem',
    filePrefix: 'w2_fixed_purchase_item_',
    description:
      '定期購入商品情報（全20項目）を登録・更新します。fixed_purchase_id+fixed_purchase_item_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'fixed_purchase_id', required: true, desc: '定期購入ID' },
      { name: 'fixed_purchase_item_no', required: true, desc: '定期購入注文商品枝番' },
      { name: 'fixed_purchase_shipping_no', required: false, desc: '定期購入配送先枝番' },
      { name: 'shop_id', required: false, desc: '店舗ID' },
      { name: 'product_id', required: false, desc: '商品ID' },
      { name: 'variation_id', required: false, desc: '商品バリエーションID' },
      { name: 'supplier_id', required: false, desc: 'サプライヤID' },
      { name: 'item_quantity', required: false, desc: '注文数' },
      { name: 'item_quantity_single', required: false, desc: '注文数（セット未考慮）' },
      { name: 'product_set_id', required: false, desc: '商品セットID' },
      { name: 'product_set_no', required: false, desc: '商品セット枝番' },
      { name: 'product_set_count', required: false, desc: '商品セット注文数' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'product_option_texts', required: false, desc: '商品付帯情報選択値' },
      { name: 'item_order_count', required: false, desc: '商品購入回数（注文基準）' },
      { name: 'item_shipped_count', required: false, desc: '商品購入回数（出荷基準）' },
      { name: 'next_time_delivery_status', required: false, desc: '次回お届けステータス' },
      { name: 'conditional_item_quantity', required: false, desc: '条件付注文数' },
      { name: 'composite_type', required: false, desc: '構成品区分' }
    ],
    sample:
      'fixed_purchase_id,fixed_purchase_item_no,fixed_purchase_shipping_no,shop_id,product_id,variation_id,supplier_id,item_quantity\n1,1,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_FixedPurchaseHistory',
    filePrefix: 'w2_fixed_purchase_history_',
    description:
      '定期購入履歴情報（全13項目）を登録・更新します。fixed_purchase_id+fixed_purchase_history_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'fixed_purchase_id', required: true, desc: '定期購入ID' },
      { name: 'fixed_purchase_history_no', required: true, desc: '定期購入注文履歴NO' },
      { name: 'fixed_purchase_history_kbn', required: false, desc: '定期購入履歴区分' },
      { name: 'base_tel_no', required: false, desc: '元電話番号' },
      { name: 'user_id', required: false, desc: 'ユーザID' },
      { name: 'order_id', required: false, desc: '注文ID' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'update_order_count', required: false, desc: '購入回数(注文基準)更新' },
      { name: 'update_shipped_count', required: false, desc: '購入回数(出荷基準)更新' },
      { name: 'update_order_count_result', required: false, desc: '購入回数(注文基準)更新結果' },
      { name: 'update_shipped_count_result', required: false, desc: '購入回数(出荷基準)更新結果' },
      { name: 'external_payment_cooperation_log', required: false, desc: '外部連携決済ログ' }
    ],
    sample:
      'fixed_purchase_id,fixed_purchase_history_no,fixed_purchase_history_kbn,base_tel_no,user_id,order_id,date_created,last_changed\n1,1,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_FixedPurchaseCancelReason',
    filePrefix: 'w2_fixed_purchase_cancel_reason_',
    description: '定期解約理由区分設定（全7項目）を登録・更新します。cancel_reason_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'cancel_reason_id', required: true, desc: '解約理由区分ID' },
      { name: 'cancel_reason_name', required: false, desc: '解約理由区分名' },
      { name: 'display_order', required: false, desc: '表示順' },
      { name: 'display_kbn', required: false, desc: '表示区分' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '更新者' }
    ],
    sample:
      'cancel_reason_id,cancel_reason_name,display_order,display_kbn,date_created,date_changed,last_changed\n1,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_UserCoupon',
    filePrefix: 'w2_user_coupon_',
    description:
      'ユーザクーポン（全14項目）を登録・更新します。user_id+dept_id+coupon_id+coupon_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'user_id', required: true, desc: 'ユーザID' },
      { name: 'dept_id', required: true, desc: '識別ID' },
      { name: 'coupon_id', required: true, desc: 'クーポンID' },
      { name: 'coupon_no', required: true, desc: '枝番' },
      { name: 'order_id', required: false, desc: '注文ID' },
      { name: 'use_flg', required: false, desc: '利用フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'user_coupon_count', required: false, desc: 'ユーザークーポン利用可能回数' },
      { name: 'cooperation_id', required: false, desc: '連携ID' },
      { name: 'cooperation_type', required: false, desc: '連携タイプ' },
      { name: 'temp_coupon_flg', required: false, desc: '仮クーポンフラグ' },
      { name: 'source_popup_id', required: false, desc: '取得元ポップアップID' }
    ],
    sample:
      'user_id,dept_id,coupon_id,coupon_no,order_id,use_flg,date_created,date_changed\n1,1,1,1,sample,sample,sample,sample'
  },
  {
    type: 'w2_UserCouponHistory',
    filePrefix: 'w2_user_coupon_history_',
    description: 'ユーザクーポン履歴（全15項目）を登録・更新します。user_id+history_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'user_id', required: true, desc: 'ユーザID' },
      { name: 'history_no', required: true, desc: '枝番' },
      { name: 'dept_id', required: false, desc: '識別ID' },
      { name: 'coupon_id', required: false, desc: 'クーポンID' },
      { name: 'coupon_code', required: false, desc: 'クーポンコード' },
      { name: 'order_id', required: false, desc: '注文ID' },
      { name: 'history_kbn', required: false, desc: 'クーポン履歴区分' },
      { name: 'action_kbn', required: false, desc: '操作区分' },
      { name: 'coupon_inc', required: false, desc: '加算数' },
      { name: 'coupon_price', required: false, desc: 'クーポン金額' },
      { name: 'memo', required: false, desc: 'メモ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'user_coupon_count', required: false, desc: 'ユーザークーポン利用可能回数' },
      { name: 'fixed_purchase_id', required: false, desc: '定期購入ID' }
    ],
    sample:
      'user_id,history_no,dept_id,coupon_id,coupon_code,order_id,history_kbn,action_kbn\n1,1,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_CouponUseUser',
    filePrefix: 'w2_coupon_use_user_',
    description:
      'クーポン利用ユーザー（全7項目）を登録・更新します。coupon_id+coupon_use_user+branch_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'coupon_id', required: true, desc: 'クーポンID' },
      { name: 'coupon_use_user', required: true, desc: 'クーポン利用ユーザー' },
      { name: 'order_id', required: false, desc: '注文ID' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'fixed_purchase_id', required: false, desc: '定期購入ID' },
      { name: 'branch_no', required: true, desc: 'クーポン利用枝番' }
    ],
    sample:
      'coupon_id,coupon_use_user,order_id,date_created,last_changed,fixed_purchase_id,branch_no\n1,1,sample,sample,sample,sample,1'
  },
  {
    type: 'w2_CouponSchedule',
    filePrefix: 'w2_coupon_schedule_',
    description:
      'クーポン発行スケジュールテーブル（全23項目）を登録・更新します。coupon_schedule_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'coupon_schedule_id', required: true, desc: 'クーポン発行スケジュールID' },
      { name: 'coupon_schedule_name', required: false, desc: 'クーポン発行スケジュール名' },
      { name: 'status', required: false, desc: 'ステータス' },
      { name: 'last_count', required: false, desc: '最終付与人数' },
      { name: 'last_exec_date', required: false, desc: '最終付与日時' },
      { name: 'target_id', required: false, desc: 'ターゲットID' },
      { name: 'target_extract_flg', required: false, desc: 'ターゲット抽出フラグ' },
      { name: 'coupon_id', required: false, desc: 'クーポン発行ID' },
      { name: 'publish_quantity', required: false, desc: 'クーポン発行枚数' },
      { name: 'mail_id', required: false, desc: 'メールテンプレートID' },
      { name: 'exec_timing', required: false, desc: '実行タイミング' },
      { name: 'schedule_kbn', required: false, desc: 'スケジュール区分' },
      { name: 'schedule_day_of_week', required: false, desc: 'スケジュール曜日' },
      { name: 'schedule_year', required: false, desc: 'スケジュール日程(年)' },
      { name: 'schedule_month', required: false, desc: 'スケジュール日程(月)' },
      { name: 'schedule_day', required: false, desc: 'スケジュール日程(日)' },
      { name: 'schedule_hour', required: false, desc: 'スケジュール日程(時)' },
      { name: 'schedule_minute', required: false, desc: 'スケジュール日程(分)' },
      { name: 'schedule_second', required: false, desc: 'スケジュール日程(秒)' },
      { name: 'valid_flg', required: false, desc: '有効フラグ' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'coupon_schedule_id,coupon_schedule_name,status,last_count,last_exec_date,target_id,target_extract_flg,coupon_id\n1,sample,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_AdvCodeLog',
    filePrefix: 'w2_advcode_log_',
    description: '広告コード流入ログ（全5項目）を登録・更新します。advcodelog_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'advcodelog_no', required: true, desc: '広告コードログNO' },
      { name: 'access_date', required: false, desc: '日付' },
      { name: 'access_time', required: false, desc: '時間' },
      { name: 'advertisement_code', required: false, desc: '広告コード' },
      { name: 'access_user_id', required: false, desc: 'アクセスユーザID' }
    ],
    sample:
      'advcodelog_no,access_date,access_time,advertisement_code,access_user_id\n1,sample,sample,sample,sample'
  },
  {
    type: 'w2_AdvCodeMediaType',
    filePrefix: 'w2_advcode_media_type_',
    description: '広告媒体区分（全6項目）を登録・更新します。advcode_media_type_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'advcode_media_type_id', required: true, desc: '広告媒体区分' },
      { name: 'advcode_media_type_name', required: false, desc: '媒体区分名' },
      { name: 'display_order', required: false, desc: '表示順' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'advcode_media_type_id,advcode_media_type_name,display_order,date_created,date_changed,last_changed\n1,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_UserActivity',
    filePrefix: 'w2_user_activity_',
    description:
      'ユーザーアクティビティ（全7項目）を登録・更新します。user_id+master_kbn+master_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'user_id', required: true, desc: 'ユーザーID' },
      { name: 'master_kbn', required: true, desc: 'マスターー区分' },
      { name: 'master_id', required: true, desc: 'マスターーID' },
      { name: 'date', required: false, desc: '日付' },
      { name: 'tgt_year', required: false, desc: '対象年' },
      { name: 'tgt_month', required: false, desc: '対象月' },
      { name: 'tgt_day', required: false, desc: '対象日' }
    ],
    sample:
      'user_id,master_kbn,master_id,date,tgt_year,tgt_month,tgt_day\n1,1,1,sample,sample,sample,sample'
  },
  {
    type: 'w2_AbTest',
    filePrefix: 'w2_ab_test_',
    description: 'A/Bテスト（全8項目）を登録・更新します。ab_test_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'ab_test_id', required: true, desc: 'ABテストID' },
      { name: 'ab_test_title', required: false, desc: 'ABテストタイトル' },
      { name: 'public_status', required: false, desc: '公開ステータス' },
      { name: 'public_start_datetime', required: false, desc: '公開開始日時' },
      { name: 'public_end_datetime', required: false, desc: '公開終了日時' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'ab_test_id,ab_test_title,public_status,public_start_datetime,public_end_datetime,date_created,date_changed,last_changed\n1,sample,sample,sample,sample,sample,sample,sample'
  },
  {
    type: 'w2_AbTestItem',
    filePrefix: 'w2_ab_test_item_',
    description: 'A/Bテストアイテム（全4項目）を登録・更新します。ab_test_id+item_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'ab_test_id', required: true, desc: 'ABテストID' },
      { name: 'item_no', required: true, desc: 'アイテム番号' },
      { name: 'page_id', required: false, desc: 'ページID' },
      { name: 'distribution_rate', required: false, desc: '出し分け比率' }
    ],
    sample: 'ab_test_id,item_no,page_id,distribution_rate\n1,1,sample,sample'
  },
  {
    type: 'w2_PageDesign',
    filePrefix: 'w2_page_design_',
    description:
      'ページデザイン ページ管理（全23項目）を登録・更新します。page_idでUPSERT。file_name+pc_file_dir_path で行動ログのページURLと突き合わせます。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'page_id', required: true, desc: '識別ID' },
      { name: 'management_title', required: false, desc: '管理用タイトル' },
      { name: 'page_type', required: false, desc: 'タイプ' },
      { name: 'file_name', required: false, desc: 'ファイル名' },
      { name: 'pc_file_dir_path', required: false, desc: 'PCディレクトリパス' },
      { name: 'group_id', required: false, desc: 'グループ識別ID' },
      { name: 'page_sort_number', required: false, desc: 'グループ内ページ順序' },
      { name: 'use_type', required: false, desc: 'ページの利用状態' },
      { name: 'publish', required: false, desc: 'ページの公開状態' },
      { name: 'condition_publish_date_from', required: false, desc: '公開開始日' },
      { name: 'condition_publish_date_to', required: false, desc: '公開終了日' },
      { name: 'condition_member_only_type', required: false, desc: '会員限定コンテンツ' },
      { name: 'condition_member_rank_id', required: false, desc: '会員ランク' },
      { name: 'condition_target_list_type', required: false, desc: 'ターゲットリスト検索条件' },
      { name: 'condition_target_list_ids', required: false, desc: '対象ターゲットリスト' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'metadata_desc', required: false, desc: 'ディスクリプション' },
      { name: 'file_time_stamp', required: false, desc: 'ファイルタイムスタンプ' },
      { name: 'sitemap_include', required: false, desc: 'サイトマップ出力要否' },
      { name: 'sitemap_changefreq', required: false, desc: 'サイトマップ更新頻度' },
      { name: 'sitemap_priority', required: false, desc: 'サイトマップ優先度' }
    ],
    sample:
      'page_id,management_title,page_type,file_name,pc_file_dir_path,group_id,page_sort_number,use_type,publish,condition_publish_date_from,condition_publish_date_to,condition_member_only_type,condition_member_rank_id,condition_target_list_type,condition_target_list_ids,date_created,date_changed,last_changed,metadata_desc,file_time_stamp,sitemap_include,sitemap_changefreq,sitemap_priority\n1,FAQページ,NORMAL,faq.html,/guide/,3,1,PC_SP,PUBLIC,,,ALL,,OR,101,102,2026/08/01 10:00:00,2026/08/01 10:00:00,operator,よくあるご質問,2026/08/01 10:00:00,INCLUDE,weekly,0.8'
  },
  {
    type: 'w2_PageDesignGroup',
    filePrefix: 'w2_page_design_group_',
    description: 'ページデザイン グループマスター（全6項目）を登録・更新します。group_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'group_id', required: true, desc: '識別ID' },
      { name: 'group_name', required: false, desc: 'グループ名' },
      { name: 'group_sort_number', required: false, desc: 'グループ順序' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'group_id,group_name,group_sort_number,date_created,date_changed,last_changed\n3,ガイド,1,2026/08/01 10:00:00,2026/08/01 10:00:00,operator'
  },
  {
    type: 'w2_PartsDesign',
    filePrefix: 'w2_parts_design_',
    description: 'パーツデザイン パーツ管理（全21項目）を登録・更新します。parts_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'parts_id', required: true, desc: '識別ID' },
      { name: 'management_title', required: false, desc: '管理用タイトル' },
      { name: 'parts_type', required: false, desc: 'タイプ' },
      { name: 'file_name', required: false, desc: 'ファイル名' },
      { name: 'pc_file_dir_path', required: false, desc: 'PCディレクトリパス' },
      { name: 'group_id', required: false, desc: 'グループ識別ID' },
      { name: 'parts_sort_number', required: false, desc: 'グループ内パーツ順序' },
      { name: 'use_type', required: false, desc: 'パーツの利用状態' },
      { name: 'publish', required: false, desc: 'パーツの公開状態' },
      { name: 'condition_publish_date_from', required: false, desc: '公開開始日' },
      { name: 'condition_publish_date_to', required: false, desc: '公開終了日' },
      { name: 'condition_member_only_type', required: false, desc: '会員限定コンテンツ' },
      { name: 'condition_member_rank_id', required: false, desc: '会員ランク' },
      { name: 'condition_target_list_type', required: false, desc: 'ターゲットリスト検索条件' },
      { name: 'condition_target_list_ids', required: false, desc: '対象ターゲットリスト' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' },
      { name: 'area_id', required: false, desc: '特集エリアID' },
      { name: 'file_time_stamp', required: false, desc: 'ファイルタイムスタンプ' },
      { name: 'vue_component_id', required: false, desc: 'VueコンポーネントID' }
    ],
    sample:
      'parts_id,management_title,parts_type,file_name,pc_file_dir_path,group_id,parts_sort_number,use_type,publish,condition_publish_date_from,condition_publish_date_to,condition_member_only_type,condition_member_rank_id,condition_target_list_type,condition_target_list_ids,date_created,date_changed,last_changed,area_id,file_time_stamp,vue_component_id\n1,共通ヘッダー,NORMAL,header.html,/parts/,2,1,PC_SP,PUBLIC,,,ALL,,OR,,2026/08/01 10:00:00,2026/08/01 10:00:00,operator,AREA01,2026/08/01 10:00:00,'
  },
  {
    type: 'w2_PartsDesignGroup',
    filePrefix: 'w2_parts_design_group_',
    description: 'パーツデザイン グループマスター（全6項目）を登録・更新します。group_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'group_id', required: true, desc: '識別ID' },
      { name: 'group_name', required: false, desc: 'グループ名' },
      { name: 'group_sort_number', required: false, desc: 'グループ順序' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'group_id,group_name,group_sort_number,date_created,date_changed,last_changed\n2,共通パーツ,1,2026/08/01 10:00:00,2026/08/01 10:00:00,operator'
  },
  {
    type: 'w2_FeatureArea',
    filePrefix: 'w2_feature_area_',
    description: '特集エリアマスター（全15項目）を登録・更新します。area_idでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'area_id', required: true, desc: '特集エリアID' },
      { name: 'area_name', required: false, desc: '特集エリア名' },
      { name: 'area_type_id', required: false, desc: '特集エリアタイプ' },
      { name: 'internal_memo', required: false, desc: '内部用メモ' },
      { name: 'side_max_count', required: false, desc: '横並び最大数' },
      { name: 'side_turn', required: false, desc: '折り返し設定' },
      { name: 'slider_count', required: false, desc: 'スライド数' },
      { name: 'slider_scroll_count', required: false, desc: 'スクロールのスライド数' },
      { name: 'slider_scroll_auto', required: false, desc: '自動スクロール' },
      { name: 'slider_scroll_interval', required: false, desc: '自動スクロールの間隔' },
      { name: 'slider_arrow', required: false, desc: '矢印表示' },
      { name: 'slider_dot', required: false, desc: 'ドット表示' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'area_id,area_name,area_type_id,internal_memo,side_max_count,side_turn,slider_count,slider_scroll_count,slider_scroll_auto,slider_scroll_interval,slider_arrow,slider_dot,date_created,date_changed,last_changed\nAREA01,トップメインバナー,SLIDER,夏キャンペーン用,3,1,3,1,1,5,1,1,2026/08/01 10:00:00,2026/08/01 10:00:00,operator'
  },
  {
    type: 'w2_FeatureAreaBanner',
    filePrefix: 'w2_feature_area_banner_',
    description: '特集エリアバナー（全16項目）を登録・更新します。area_id+banner_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'area_id', required: true, desc: '特集エリアID' },
      { name: 'banner_no', required: true, desc: '特集エリアバナーNo' },
      { name: 'file_name', required: false, desc: 'ファイル名' },
      { name: 'file_dir_path', required: false, desc: 'ディレクトリパス' },
      { name: 'alt_text', required: false, desc: '代替テキスト' },
      { name: 'text', required: false, desc: 'テキスト' },
      { name: 'link_url', required: false, desc: 'リンク' },
      { name: 'window_type', required: false, desc: 'ウィンドウタイプ' },
      { name: 'publish', required: false, desc: '公開状態' },
      { name: 'condition_publish_date_from', required: false, desc: '公開開始日' },
      { name: 'condition_publish_date_to', required: false, desc: '公開終了日' },
      { name: 'condition_member_only_type', required: false, desc: '会員限定コンテンツ' },
      { name: 'condition_member_rank_id', required: false, desc: '会員ランク' },
      { name: 'condition_target_list_type', required: false, desc: 'ターゲットリスト検索条件' },
      { name: 'condition_target_list_ids', required: false, desc: '対象ターゲットリスト' },
      { name: 'date_created', required: false, desc: '作成日' }
    ],
    sample:
      'area_id,banner_no,file_name,file_dir_path,alt_text,text,link_url,window_type,publish,condition_publish_date_from,condition_publish_date_to,condition_member_only_type,condition_member_rank_id,condition_target_list_type,condition_target_list_ids,date_created\nAREA01,1,banner1.jpg,/img/banner/,夏キャンペーン,夏の新作,/feature/summer,NONPOPUP,PUBLIC,,,ALL,,OR,101,2026/08/01 10:00:00'
  },
  {
    type: 'w2_Popup',
    filePrefix: 'w2_popup_',
    description:
      'ポップアップ（全20項目）を登録・更新します。popup_idでUPSERT。condition_target_list_ids でターゲットリスト別の出し分けが分かります。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'popup_id', required: true, desc: 'ポップアップID' },
      { name: 'popup_name', required: false, desc: 'ポップアップ名' },
      { name: 'popup_type', required: false, desc: 'ポップアップタイプ' },
      { name: 'public_status', required: false, desc: '公開状態' },
      { name: 'public_start_datetime', required: false, desc: '公開開始日時' },
      { name: 'public_end_datetime', required: false, desc: '公開終了日時' },
      { name: 'condition_member_only_type', required: false, desc: '会員限定コンテンツ' },
      { name: 'condition_member_rank_id', required: false, desc: '会員ランク' },
      { name: 'condition_target_list_type', required: false, desc: 'ターゲットリスト検索条件' },
      { name: 'condition_target_list_ids', required: false, desc: '対象ターゲットリスト' },
      { name: 'condition_advertisement_codes', required: false, desc: '広告コード' },
      { name: 'condition_available_device', required: false, desc: '利用デバイス' },
      { name: 'display_trigger_setting_type', required: false, desc: '表示トリガータイプ' },
      { name: 'display_trigger_setting_value', required: false, desc: 'トリガーとなる値' },
      { name: 'display_priority', required: false, desc: '表示優先順' },
      { name: 'design_setting', required: false, desc: 'デザイン設定' },
      { name: 'contents_setting', required: false, desc: 'コンテンツ設定' },
      { name: 'date_created', required: false, desc: '作成日' },
      { name: 'date_changed', required: false, desc: '更新日' },
      { name: 'last_changed', required: false, desc: '最終更新者' }
    ],
    sample:
      'popup_id,popup_name,popup_type,public_status,public_start_datetime,public_end_datetime,condition_member_only_type,condition_member_rank_id,condition_target_list_type,condition_target_list_ids,condition_advertisement_codes,condition_available_device,display_trigger_setting_type,display_trigger_setting_value,display_priority,design_setting,contents_setting,date_created,date_changed,last_changed\nPOP001,初回限定クーポン,CENTER_BOX,PUBLISHED,2026/08/01 00:00:00,2026/08/31 23:59:59,ALL,,OR,101,102,ADV001,ALL,ELAPSED_TIME,5,1,{},{},2026/08/01 10:00:00,2026/08/01 10:00:00,operator'
  },
  {
    type: 'w2_PopupDisplayPageSetting',
    filePrefix: 'w2_popup_display_page_setting_',
    description:
      'ポップアップ表示ページ設定（全9項目）を登録・更新します。popup_id+type+data_noでUPSERT。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'popup_id', required: true, desc: 'ポップアップID' },
      { name: 'type', required: true, desc: 'タイプ' },
      { name: 'data_no', required: true, desc: 'データNo' },
      { name: 'target_page', required: false, desc: '任意指定のページ' },
      { name: 'product_id', required: false, desc: '商品ID' },
      { name: 'category_id', required: false, desc: 'カテゴリID' },
      { name: 'page_id', required: false, desc: 'LPページID' },
      { name: 'brand_id', required: false, desc: 'ブランドID' },
      { name: 'target_url', required: false, desc: 'URL' }
    ],
    sample:
      'popup_id,type,data_no,target_page,product_id,category_id,page_id,brand_id,target_url\nPOP001,PRODUCT,1,,P0001,,,,'
  },
  {
    type: 'w2_PopupReport',
    filePrefix: 'w2_popup_report_',
    description:
      'ポップアップレポート（全9項目）を登録・更新します。data_noでUPSERT。日付×ポップアップIDの日次集計値で、顧客単位ではありません。',
    behavior: 'UPSERT（追加/更新）',
    columns: [
      { name: 'data_no', required: true, desc: 'データNO' },
      { name: 'date', required: false, desc: '日付' },
      { name: 'tgt_year', required: false, desc: '対象年' },
      { name: 'tgt_month', required: false, desc: '対象月' },
      { name: 'tgt_day', required: false, desc: '対象日' },
      { name: 'report_type', required: false, desc: 'レポートタイプ' },
      { name: 'access_kbn', required: false, desc: 'アクセス区分' },
      { name: 'popup_id', required: false, desc: 'ポップアップID' },
      { name: 'count', required: false, desc: 'カウント' }
    ],
    sample:
      'data_no,date,tgt_year,tgt_month,tgt_day,report_type,access_kbn,popup_id,count\n1,2026/08/01 00:00:00,2026,08,01,DISPLAY,PC,POP001,1234'
  }
]

export const CSV_FORMATS: CsvFormat[] = RAW_CSV_FORMATS.map((f) => ({
  ...f,
  label: masterTypeName(f.type)
}))
